// src/screens/MitoDespachoScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";

import "./mitodespacho.css";

const PAD2 = (n) => String(n).padStart(2, "0");

const ENV =
  typeof process !== "undefined" && process && process.env ? process.env : {};

const MITO_FINALIZADOS_ENDPOINT =
  ENV.REACT_APP_MITO_SERVICIOS_FINALIZADOS_GRAFICOS_URL ||
  "https://api.medic.com.ar/api/servicios/obtenerserviciosfinalizadosgraficos";

const MITO_PRODUCTIVIDAD_ENDPOINT =
  ENV.REACT_APP_MITO_SERVICIOS_PRODUCTIVIDAD_URL ||
  "https://api.medic.com.ar/api/servicios/obtenerserviciosproductividad";

const MITO_API_TOKEN = ENV.REACT_APP_MITO_API_TOKEN || "";

const POR_MOVIL_MANUAL_STORAGE_KEY = "mito_por_movil_manual_v2";
const FIXED_HOUR_FORMAT = "24";
const FIXED_OPERATIONAL_START_HOUR = 6;
const FIXED_OPERATIONAL_END_HOUR = 6;

function safeGetLocalStorage(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function getStoredToken() {
  return (
    safeGetLocalStorage("MITO_API_TOKEN") ||
    safeGetLocalStorage("mito_token") ||
    safeGetLocalStorage("medic_token") ||
    safeGetLocalStorage("token") ||
    ""
  );
}

function safeSetLocalStorage(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, value);
  } catch {}
}

function safeParseJSON(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function getStoredPorMovilManual() {
  const parsed = safeParseJSON(
    safeGetLocalStorage(POR_MOVIL_MANUAL_STORAGE_KEY),
    {},
  );
  return parsed && typeof parsed === "object" ? parsed : {};
}

function buildPorMovilManualKey(fechaISO, base, movil) {
  return `${String(fechaISO || "").trim()}__${normalizeCompareText(base)}__${String(movil || "").trim()}`;
}

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${PAD2(d.getMonth() + 1)}-${PAD2(d.getDate())}`;
}

const MITO_MIN_DATE = "2026-03-01";

function clampFechaISO(value) {
  const v = String(value || "").trim();
  if (!v) return MITO_MIN_DATE;
  return v < MITO_MIN_DATE ? MITO_MIN_DATE : v;
}

function labelFecha(iso) {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("es-AR", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function labelFechaPlanilla(iso) {
  try {
    const [y, m, d] = String(iso || "")
      .split("-")
      .map(Number);
    return `Fecha: ${d}-${m}-${y}`;
  } catch {
    return `Fecha: ${iso || ""}`;
  }
}

function formatDateOnlyShort(iso) {
  const raw = String(iso || "").trim();
  if (!raw) return "";
  const [y, m, d] = raw.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return raw;
  return `${PAD2(d)}/${PAD2(m)}/${y}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function makeExportCell(value, className = "") {
  const cls = className ? ` class="${escapeHtml(className)}"` : "";
  return `<td${cls}>${escapeHtml(value)}</td>`;
}

function makeExportHeader(value, className = "") {
  const cls = className ? ` class="${escapeHtml(className)}"` : "";
  return `<th${cls}>${escapeHtml(value)}</th>`;
}

function getExportBaseName(fechaISO) {
  return `mito_${String(fechaISO || isoToday()).replaceAll("-", "")}`;
}

function downloadBlob(blob, filename) {
  if (typeof document === "undefined" || typeof URL === "undefined") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function excelColumnName(index) {
  let n = index + 1;
  let name = "";
  while (n > 0) {
    const mod = (n - 1) % 26;
    name = String.fromCharCode(65 + mod) + name;
    n = Math.floor((n - mod) / 26);
  }
  return name;
}

function escapeXml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function utf8Bytes(text) {
  return new TextEncoder().encode(String(text ?? ""));
}

const ZIP_CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = ZIP_CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUint16LE(array, offset, value) {
  array[offset] = value & 0xff;
  array[offset + 1] = (value >>> 8) & 0xff;
}

function writeUint32LE(array, offset, value) {
  array[offset] = value & 0xff;
  array[offset + 1] = (value >>> 8) & 0xff;
  array[offset + 2] = (value >>> 16) & 0xff;
  array[offset + 3] = (value >>> 24) & 0xff;
}

function concatUint8Arrays(parts) {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

function getZipDosDateTime() {
  const now = new Date();
  const year = Math.max(1980, now.getFullYear());
  const dosTime =
    (now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
  return { dosTime, dosDate };
}

function createZipBlob(files) {
  const localParts = [];
  const centralParts = [];
  let offset = 0;
  const { dosTime, dosDate } = getZipDosDateTime();

  for (const file of files) {
    const nameBytes = utf8Bytes(file.name);
    const dataBytes = file.data instanceof Uint8Array ? file.data : utf8Bytes(file.data);
    const fileCrc = crc32(dataBytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    writeUint32LE(localHeader, 0, 0x04034b50);
    writeUint16LE(localHeader, 4, 20);
    writeUint16LE(localHeader, 6, 0x0800);
    writeUint16LE(localHeader, 8, 0);
    writeUint16LE(localHeader, 10, dosTime);
    writeUint16LE(localHeader, 12, dosDate);
    writeUint32LE(localHeader, 14, fileCrc);
    writeUint32LE(localHeader, 18, dataBytes.length);
    writeUint32LE(localHeader, 22, dataBytes.length);
    writeUint16LE(localHeader, 26, nameBytes.length);
    writeUint16LE(localHeader, 28, 0);
    localHeader.set(nameBytes, 30);

    localParts.push(localHeader, dataBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    writeUint32LE(centralHeader, 0, 0x02014b50);
    writeUint16LE(centralHeader, 4, 20);
    writeUint16LE(centralHeader, 6, 20);
    writeUint16LE(centralHeader, 8, 0x0800);
    writeUint16LE(centralHeader, 10, 0);
    writeUint16LE(centralHeader, 12, dosTime);
    writeUint16LE(centralHeader, 14, dosDate);
    writeUint32LE(centralHeader, 16, fileCrc);
    writeUint32LE(centralHeader, 20, dataBytes.length);
    writeUint32LE(centralHeader, 24, dataBytes.length);
    writeUint16LE(centralHeader, 28, nameBytes.length);
    writeUint16LE(centralHeader, 30, 0);
    writeUint16LE(centralHeader, 32, 0);
    writeUint16LE(centralHeader, 34, 0);
    writeUint16LE(centralHeader, 36, 0);
    writeUint32LE(centralHeader, 38, 0);
    writeUint32LE(centralHeader, 42, offset);
    centralHeader.set(nameBytes, 46);
    centralParts.push(centralHeader);

    offset += localHeader.length + dataBytes.length;
  }

  const centralDirectory = concatUint8Arrays(centralParts);
  const endRecord = new Uint8Array(22);
  writeUint32LE(endRecord, 0, 0x06054b50);
  writeUint16LE(endRecord, 4, 0);
  writeUint16LE(endRecord, 6, 0);
  writeUint16LE(endRecord, 8, files.length);
  writeUint16LE(endRecord, 10, files.length);
  writeUint32LE(endRecord, 12, centralDirectory.length);
  writeUint32LE(endRecord, 16, offset);
  writeUint16LE(endRecord, 20, 0);

  return new Blob([...localParts, centralDirectory, endRecord], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function sanitizeSheetName(name, index) {
  const cleaned = String(name || `Hoja ${index + 1}`)
    .replace(/[\\/?*:[\]]/g, " ")
    .slice(0, 31)
    .trim();
  return cleaned || `Hoja ${index + 1}`;
}

function parseSpreadsheetXmlToSheets(xmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  const parserError = doc.getElementsByTagName("parsererror")[0];
  if (parserError) throw new Error("No se pudo preparar el archivo Excel");

  const worksheets = Array.from(doc.getElementsByTagName("Worksheet"));
  return worksheets.map((worksheet, sheetIndex) => {
    const name =
      worksheet.getAttribute("ss:Name") ||
      worksheet.getAttribute("Name") ||
      `Hoja ${sheetIndex + 1}`;
    const rows = Array.from(worksheet.getElementsByTagName("Row")).map((row) =>
      Array.from(row.getElementsByTagName("Cell")).map((cell) => {
        const data = cell.getElementsByTagName("Data")[0];
        const type = data?.getAttribute("ss:Type") || data?.getAttribute("Type") || "String";
        const rawValue = data?.textContent || "";
        const numericValue = Number(String(rawValue).replace(",", "."));
        return {
          value: rawValue,
          type: type === "Number" && Number.isFinite(numericValue) ? "number" : "string",
        };
      }),
    );
    return { name: sanitizeSheetName(name, sheetIndex), rows };
  });
}

function buildWorksheetXml(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const body = safeRows
    .map((row, rowIndex) => {
      const cells = (Array.isArray(row) ? row : [])
        .map((cell, colIndex) => {
          const ref = `${excelColumnName(colIndex)}${rowIndex + 1}`;
          const value = cell?.value ?? "";
          if (cell?.type === "number" && value !== "") {
            return `<c r="${ref}"><v>${escapeXml(value)}</v></c>`;
          }
          return `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
        })
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <sheetData>${body}</sheetData>
</worksheet>`;
}

function createXlsxBlobFromSheets(sheets) {
  const safeSheets = sheets.length ? sheets : [{ name: "Planillas", rows: [] }];
  const sheetContentTypes = safeSheets
    .map(
      (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join("");
  const workbookSheets = safeSheets
    .map(
      (sheet, index) =>
        `<sheet name="${escapeXml(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`,
    )
    .join("");
  const workbookRels = safeSheets
    .map(
      (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`,
    )
    .join("");

  const files = [
    {
      name: "[Content_Types].xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheetContentTypes}
</Types>`,
    },
    {
      name: "_rels/.rels",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      name: "xl/workbook.xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${workbookRels}
  <Relationship Id="rId${safeSheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    {
      name: "xl/styles.xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="1"><font><sz val="10"/><name val="Arial"/></font></fonts>
  <fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`,
    },
    ...safeSheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      data: buildWorksheetXml(sheet.rows),
    })),
  ];

  return createZipBlob(files);
}

async function pickExportFolder(folderName) {
  if (
    typeof window === "undefined" ||
    typeof window.showDirectoryPicker !== "function"
  ) {
    return null;
  }

  const rootHandle = await window.showDirectoryPicker({ mode: "readwrite" });
  try {
    return await rootHandle.getDirectoryHandle(folderName, { create: true });
  } catch {
    return rootHandle;
  }
}

async function saveBlobToDirectory(directoryHandle, filename, blob) {
  if (!directoryHandle) return;
  const fileHandle = await directoryHandle.getFileHandle(filename, {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("No se pudo leer la imagen"));
    reader.readAsDataURL(blob);
  });
}

function imageToJpegBlob(image, width, height, quality = 0.96) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo preparar el canvas de exportación");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("No se pudo generar el JPG"));
    }, "image/jpeg", quality);
  });
}

async function nodeToJpegBlob(node, quality = 0.96, scale = 2) {
  if (!node) throw new Error("No hay contenido para exportar");
  const rect = node.getBoundingClientRect();
  const width = Math.max(1, Math.ceil(rect.width));
  const height = Math.max(1, Math.ceil(rect.height));
  const clonedNode = node.cloneNode(true);
  clonedNode.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");

  const styles = Array.from(document.styleSheets)
    .map((styleSheet) => {
      try {
        return Array.from(styleSheet.cssRules || []).map((rule) => rule.cssText).join("\n");
      } catch {
        return "";
      }
    })
    .join("\n");

  const serialized = new XMLSerializer().serializeToString(clonedNode);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width * scale}" height="${height * scale}" viewBox="0 0 ${width} ${height}">
      <foreignObject width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml">
          <style>${styles}</style>
          ${serialized}
        </div>
      </foreignObject>
    </svg>`;

  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const dataUrl = await blobToDataURL(svgBlob);
  const img = new Image();
  img.decoding = "async";
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = () => reject(new Error("No se pudo convertir la planilla a imagen"));
    img.src = dataUrl;
  });
  return imageToJpegBlob(img, width * scale, height * scale, quality);
}

function hhmmss(date) {
  if (!date) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

function parseAnyDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return direct;

  const slashMatch = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (slashMatch) {
    const [, mm, dd, yyyy, hh, min, ss = "00"] = slashMatch;
    const parsed = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      Number(ss),
    );
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
}

function computeDemoraMin(fromValue, toValue) {
  const fromDate = parseAnyDate(fromValue);
  const toDate = parseAnyDate(toValue);
  if (!fromDate || !toDate) return "";

  const diffMs = toDate.getTime() - fromDate.getTime();
  if (diffMs < 0) return "";

  return Math.round(diffMs / 60000);
}

function formatDemoraDisplay(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const numeric = Number(raw.replace(/[^\d.-]/g, ""));
  if (Number.isFinite(numeric)) return `${Math.round(numeric)}´`;

  return raw;
}

function parseHHMM(hhmm) {
  const onlyHour = String(hhmm || "00:00").slice(0, 5);
  const [h, m] = onlyHour.split(":").map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

function inRangeWrap(min, startMin, endMin) {
  if (startMin <= endMin) return min >= startMin && min < endMin;
  return min >= startMin || min < endMin;
}

function timeBucket(hhmm) {
  const t = parseHHMM(hhmm);
  if (t >= 0 && t < 6 * 60) return "00:00 a 06:00";
  if (t >= 6 * 60 && t < 12 * 60) return "06:00 a 12:00";
  if (t >= 12 * 60 && t < 18 * 60) return "12:00 a 18:00";
  return "18:00 a 24:00";
}

function formatClockValue(value, hourFormat = "24") {
  const raw = String(value || "").trim();
  if (!raw) return "—";

  const match = raw.match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return raw;

  const [, hh, mm, ss] = match;
  const hours = Number(hh);
  const minutes = Number(mm);
  const seconds = ss !== undefined ? Number(ss) : null;

  if (hourFormat === "24") {
    return seconds === null
      ? `${PAD2(hours)}:${PAD2(minutes)}`
      : `${PAD2(hours)}:${PAD2(minutes)}:${PAD2(seconds)}`;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return seconds === null
    ? `${PAD2(hours12)}:${PAD2(minutes)} ${period}`
    : `${PAD2(hours12)}:${PAD2(minutes)}:${PAD2(seconds)} ${period}`;
}

function formatDateClock(date, hourFormat = "24") {
  if (!date) return "—";
  const h = date.getHours();
  const m = date.getMinutes();
  const s = date.getSeconds();
  return formatClockValue(`${PAD2(h)}:${PAD2(m)}:${PAD2(s)}`, hourFormat);
}

function formatDateTimeValue(value, hourFormat = "24") {
  const parsed = parseAnyDate(value);
  if (!parsed) return String(value || "—");
  const datePart = new Intl.DateTimeFormat("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(parsed);
  const timePart = new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: hourFormat === "12",
  }).format(parsed);
  return `${datePart} ${timePart}`;
}

function formatBucketLabel(bucket, hourFormat = "24") {
  const raw = String(bucket || "").trim();
  if (!raw.includes(" a ")) return raw;
  const [desde, hasta] = raw.split(" a ").map((part) => part.trim());
  return `${formatClockValue(`${desde}:00`, hourFormat)} a ${formatClockValue(`${hasta}:00`, hourFormat)}`;
}

function normalizeHourStart(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 6;
  return ((Math.trunc(parsed) % 24) + 24) % 24;
}

function getOperationalRangeLabel(startHour, hourFormat = "24") {
  const normalizedStart = normalizeHourStart(startHour);
  const normalizedEnd = normalizeHourStart(normalizedStart + 24);
  return `${formatClockValue(`${PAD2(normalizedStart)}:00:00`, hourFormat)} a ${formatClockValue(`${PAD2(normalizedEnd)}:00:00`, hourFormat)}`;
}

function buildPeriodStrings(fechaISO, startHour) {
  try {
    const [y, m, d] = String(fechaISO || isoToday())
      .split("-")
      .map(Number);
    const start = new Date(
      y,
      (m || 1) - 1,
      d || 1,
      normalizeHourStart(startHour),
      0,
      0,
    );
    const end = new Date(start);
    end.setHours(end.getHours() + 24, 0, 0, 0);
    return {
      periodDesde: `${start.getFullYear()}-${PAD2(start.getMonth() + 1)}-${PAD2(start.getDate())} ${PAD2(start.getHours())}:00`,
      periodHasta: `${end.getFullYear()}-${PAD2(end.getMonth() + 1)}-${PAD2(end.getDate())} ${PAD2(end.getHours())}:00`,
    };
  } catch {
    const startLabel = `${fechaISO} ${PAD2(normalizeHourStart(startHour))}:00`;
    const endLabel = `${fechaISO} ${PAD2(normalizeHourStart(Number(startHour) + 24))}:00`;
    return {
      periodDesde: startLabel,
      periodHasta: endLabel,
    };
  }
}

function ColorBadge({ color }) {
  const cls =
    color === "ROJO"
      ? "rojo"
      : color === "AMARILLO"
        ? "amarillo"
        : color === "VERDE"
          ? "verde"
          : color === "FUCSIA"
            ? "fuccia"
            : color === "CELESTE"
              ? "azul"
              : "blanco";

  return <span className={`mito-badge ${cls}`}>{color || "—"}</span>;
}

const PRESTADORES_ZONA_MEDIC = [
  "CMC",
  "INTERZONAS",
  "Dr. Rodriguez",
  "Dr. Pardal",
  "Dr. Rojas",
  "Dr. Carrion",
];
const PRESTADORES_FUERA_ZONA = [
  "UC San Martin",
  "Vittal",
  "Max salud",
  "Cardio sur",
  "Semp",
  "Presencia medica",
  "Priormed",
  "Swiss medical",
  "Ermed",
  "Mas Vida",
];

const PRESTADOR_SIN_IDENTIFICAR_LABEL = "Prestador sin identificar";

const SOCIOS_TRASLADO = [
  "HOMINNIS",
  "AGUERO",
  "SWISS MEDICAL",
  "EMERGER",
  "AMTA",
  "San Juan de Dios",
  "PAMI",
  "galeno",
  "Mastered",
  "SAME",
  "Vitas",
  "Áreas Protegida",
  "Cardio",
  "Solis (S.H. San Justo. S.A)",
  "Sifeme",
  "Sanatorio Mariano Acosta",
  "Interzona",
  "Doctor Red",
  "Gerdana",
  "Vittal",
  "Socio directo",
  "Uom",
  "Evento",
];

const BASE_HORARIOS_DEFAULT = [
  { match: "CASANOVA", horario: "06:00 a 06:00" },
  { match: "BONZI", horario: "07:00 a 07:00" },
  { match: "MADERO", horario: "07:00 a 07:00" },
  { match: "EL PALOMAR", horario: "07:00 a 07:00" },
  { match: "LOMA HERMOSA", horario: "07:00 a 07:00" },
  { match: "M CORONADO", horario: "07:00 a 07:00" },
  { match: "MERLO", horario: "07:00 a 07:00" },
  { match: "MORON", horario: "07:00 a 07:00" },
  { match: "R CASTILLO", horario: "07:00 a 07:00" },
  { match: "MEDIANA 24", horario: "07:00 a 07:00" },
  { match: "MEDIANA", horario: "07:00 a 07:00" },
  { match: "SAN JUSTO 2", horario: "07:00 a 07:00" },
  { match: "SAN JUSTO", horario: "07:00 a 07:00" },
  { match: "VERGARA", horario: "07:00 a 07:00" },
  { match: "CASEROS", horario: "07:00 a 07:00" },
  { match: "UTIM 16", horario: "08:00 a 00:00" },
  { match: "UTIM 20", horario: "08:00 a 00:00" },
  { match: "UTIM 22", horario: "08:00 a 00:00" },
  { match: "UTIM 24", horario: "08:00 a 00:00" },
  { match: "UTIM 28", horario: "08:00 a 00:00" },
  { match: "UTIM 30", horario: "08:00 a 00:00" },
  { match: "UTIM 32", horario: "08:00 a 00:00" },
  { match: "UTIM 33", horario: "08:00 a 00:00" },
  { match: "UTIM 35", horario: "08:00 a 00:00" },
  { match: "UTIM 42", horario: "08:00 a 00:00" },
  { match: "UTIM 44", horario: "08:00 a 00:00" },
  { match: "UTIM 47", horario: "08:00 a 00:00" },
  { match: "RAMOS MEJIA", horario: "10:00 a 22:00" },
  { match: "LAFERRERE", horario: "09:00 a 21:00" },
  { match: "APOYO 12", horario: "12:00 a 00:00" },
  { match: "APOYO UTIM NOCTURNA", horario: "" },
  { match: "UTIM 12", horario: "" },
  { match: "VISITAS 12", horario: "" },
  { match: "VISITAS 16", horario: "" },
  { match: "VISITAS 24", horario: "" },
];

const BASES_OPERATIVAS_CONFIG = [
  {
    label: "B. I. CASANOVA",
    aliases: ["B. I. CASANOVA", "B I CASANOVA", "CASANOVA"],
  },
  { label: "B. A. BONZI", aliases: ["B. A. BONZI", "B A BONZI", "BONZI"] },
  { label: "B. C. MADERO", aliases: ["B. C. MADERO", "B C MADERO", "MADERO"] },
  { label: "B. EL PALOMAR", aliases: ["B. EL PALOMAR", "B EL PALOMAR", "EL PALOMAR"] },
  {
    label: "B. LAFERRERE",
    aliases: ["B. LAFERRERE", "B LAFERRERE", "LAFERRERE"],
  },
  { label: "B. LOMA HERMOSA", aliases: ["B. LOMA HERMOSA", "B LOMA HERMOSA", "LOMA HERMOSA"] },
  { label: "B. M. CORONADO", aliases: ["B. M. CORONADO", "B M CORONADO", "M CORONADO", "MARTIN CORONADO"] },
  { label: "B. MERLO", aliases: ["B. MERLO", "B MERLO", "MERLO"] },
  { label: "B. MORON", aliases: ["B. MORON", "B MORON", "MORON"] },
  { label: "B. R. CASTILLO", aliases: ["B. R. CASTILLO", "B R CASTILLO", "R CASTILLO", "RAFAEL CASTILLO"] },
  {
    label: "B. RAMOS MEJIA",
    aliases: ["B. RAMOS MEJIA", "B RAMOS MEJIA", "RAMOS MEJIA"],
  },
  {
    label: "B. SAN JUSTO",
    aliases: ["B. SAN JUSTO", "B SAN JUSTO", "SAN JUSTO"],
  },
  {
    label: "B. SAN JUSTO 2",
    aliases: ["B. SAN JUSTO 2", "B SAN JUSTO 2", "SAN JUSTO 2"],
  },
  { label: "B. VERGARA", aliases: ["B. VERGARA", "B VERGARA", "VERGARA"] },
  {
    label: "B.Caseros",
    aliases: ["B.CASEROS", "B. CASEROS", "B CASEROS", "CASEROS"],
  },
  { label: "MEDIANA COMPLEJIDAD", aliases: ["MEDIANA COMPLEJIDAD", "MEDIANA"] },
  { label: "MEDIANA 24hs", aliases: ["MEDIANA COMPLEJIDAD 24 HS", "MEDIANA 24HS", "MEDIANA 24 HS"] },
  { label: "M. VISITAS 12 HS", aliases: ["M. VISITAS 12 HS", "M VISITAS 12 HS", "VISITAS 12 HS"] },
  {
    label: "M. VISITAS 16 HS",
    aliases: ["M. VISITAS 16 HS", "M VISITAS 16 HS", "VISITAS 16 HS"],
  },
  { label: "M. VISITAS 24 HS", aliases: ["M. VISITAS 24 HS", "M VISITAS 24 HS", "VISITAS 24 HS"] },
  { label: "Utim 12hs", aliases: ["UTIM 12HS", "UTIM 12 HS", "UTIM 12"] },
  {
    label: "Utim 16 Hs 1",
    aliases: ["UTIM 16 HS 1", "UTIM 16HS 1", "UTIM 16 1"],
  },
  {
    label: "Utim 16 Hs 2",
    aliases: ["UTIM 16 HS 2", "UTIM 16HS 2", "UTIM 16 2", "UTIM 16HS 2"],
  },
  { label: "UTIM 20", aliases: ["UTIM 20"] },
  { label: "UTIM 22", aliases: ["UTIM 22"] },
  { label: "UTIM 24", aliases: ["UTIM 24"] },
  { label: "UTIM 28", aliases: ["UTIM 28"] },
  { label: "UTIM 30", aliases: ["UTIM 30"] },
  { label: "UTIM 32", aliases: ["UTIM 32"] },
  { label: "UTIM 33", aliases: ["UTIM 33"] },
  { label: "UTIM 35", aliases: ["UTIM 35"] },
  { label: "UTIM 42", aliases: ["UTIM 42"] },
  { label: "UTIM 44", aliases: ["UTIM 44"] },
  { label: "UTIM 47", aliases: ["UTIM 47"] },
  {
    label: "Apoyo Utim Nocturna",
    aliases: ["APOYO UTIM NOCTURNA", "APOYO UTIM"],
  },
];

const BASES_OPERATIVAS_ORDER = new Map(
  BASES_OPERATIVAS_CONFIG.map((item, index) => [item.label, index]),
);

const SOCIO_ALIAS_CONFIG = [
  { label: "Socio directo", aliases: ["SOCIO DIRECTO"] },
  {
    label: "SAME",
    aliases: ["107- SAME- LA MATANZA", "107- SAME- TRES DE FEBRERO"],
  },
  {
    label: "EMERGER",
    aliases: [
      "EMERGER",
      "EMERGER EMERGENCIAS MEDICAS",
      "EMERGER  (SERVICIOS SAN COR SALUD)",
    ],
  },
  { label: "HOMINNIS", aliases: ["HOMINNIS", "MEDICINA PREPAGA HOMINIS S.A."] },
  { label: "AGUERO", aliases: ["CLINICA AGUERO"] },
  {
    label: "SWISS MEDICAL",
    aliases: ["SWISS MEDICAL SA", "SWISS MEDICAL S.A."],
  },
  { label: "AMTA", aliases: ["A.M.T.A"] },
  {
    label: "San Juan de Dios",
    aliases: ["SAN JUAN DE DIOS", "CASA HOSPITAL SAN JUAN DE DIOS"],
  },
  { label: "PAMI", aliases: ["PAMI", "D.A.M.N.P Y P"] },
  { label: "galeno", aliases: ["GALENO"] },
  { label: "Mastered", aliases: ["EMERGENCIAS NORTE S.A.", "MASTER RED S.A.", "MASTER RED"] },
  { label: "Vitas", aliases: ["VITAS S.A."] },
  {
    label: "Áreas Protegida",
    aliases: ["AREAS PROTEGIDA", "AREA PROTEGIDA", "AREAS PROTEGIDAS"],
  },
  {
    label: "Cardio",
    aliases: [
      "CARDIO",
      "SERVICIOS HOSPITALARIOS SAN JUSTO S.A.",
      "CARDIO SUR S.A.",
    ],
  },
  {
    label: "Solis (S.H. San Justo. S.A)",
    aliases: ["SOLIS", "S.H. SAN JUSTO", "SH SAN JUSTO"],
  },
  { label: "Sifeme", aliases: ["SIFEME", "SIFEME S.A.", "SIFEME SA"] },
  {
    label: "Sanatorio Mariano Acosta",
    aliases: ["SANATORIO PRIVADO MARIANO ACOSTA S.A."],
  },
  { label: "Interzona", aliases: ["INTERZONA", "INTERZONAS"] },
  { label: "Doctor Red", aliases: ["DOCTOR RED", "DOCTORED", "ROI S.A"] },
  { label: "Gerdana", aliases: ["GERDANNA S.A.", "GERDANNA SALUD"] },
  {
    label: "Vittal",
    aliases: [
      "VITTAL",
      "SOCORRO MEDICO PRIVADO S A (VITTAL)",
      "SOCORRO MEDICO PRIVADO SA (VITTAL)",
      "VITTAL EMERGENCIAS",
    ],
  },
  {
    label: "Uom",
    aliases: ["UOM", "METALURGICOS DE MATANZA SERVICIOS DE SALUD S.R.L."],
  },
  { label: "Evento", aliases: ["EVENTO"] },
];

const PRESTADOR_ALIAS_CONFIG = [
  { label: "CMC", aliases: ["CMC", "CMC-PROMEDICA INTER."] },
  { label: "INTERZONAS", aliases: ["INTERZONA", "INTERZONAS"] },
  {
    label: "Dr. Rodriguez",
    aliases: ["DR RODRIGUEZ", "DR. RODRIGUEZ", "RODRIGUEZ"],
  },
  { label: "Dr. Pardal", aliases: ["DR PARDAL", "DR. PARDAL", "PARDAL"] },
  { label: "Dr. Rojas", aliases: ["DR ROJAS", "DR. ROJAS", "ROJAS"] },
  { label: "Dr. Carrion", aliases: ["PRESTADOR", ] },
  {
    label: "UC San Martin",
    aliases: ["UC SAN MARTIN", "UNIDAD CORONARIA SAN MARTIN", "UCM SAN MARTIN"],
  },
  {
    label: "Vittal",
    aliases: [
      "VITTAL",
      "SOCORRO MEDICO PRIVADO S A (VITTAL)",
      "SOCORRO MEDICO PRIVADO SA (VITTAL)",
    ],
  },
  { label: "Max salud", aliases: ["MAX SALUD"] },
  { label: "Cardio sur", aliases: ["CARDIO SUR S.A."] },
  { label: "Semp", aliases: ["SEMP"] },
  {
    label: "Presencia medica",
    aliases: [
      "SIPREM",
      "PRESENCIA MEDICA S.A.",
      "PRES.MED.MORENO 0237-4629555/4633444",
      "PRES.MED.MORENO",
    ],
  },
  { label: "Priormed", aliases: ["PRIORMED"] },
  {
    label: "Swiss medical",
    aliases: ["SWISS MEDICAL SA", "SWISS MEDICAL S.A."],
  },
  { label: "Ermed", aliases: ["ERMED"] },
  { label: "Mas Vida", aliases: ["MAS VIDA"] },
];

function pickFirst(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return "";
}

function toUpperText(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function normalizeColorValue(value) {
  const normalized = toUpperText(value);
  if (!normalized) return "";
  if (
    normalized === "FUCSIA" ||
    normalized === "FUSCIA" ||
    normalized === "FUCCIA"
  )
    return "FUCSIA";
  return normalized;
}

function formatApiDatePart(date) {
  return `${PAD2(date.getMonth() + 1)}/${PAD2(date.getDate())}/${date.getFullYear()}`;
}

function buildMitoRange(fechaISO, startHour = FIXED_OPERATIONAL_START_HOUR) {
  const [y, m, d] = String(fechaISO || isoToday())
    .split("-")
    .map(Number);
  const from = new Date(
    y,
    (m || 1) - 1,
    d || 1,
    normalizeHourStart(startHour),
    0,
    0,
  );
  const to = new Date(from);
  to.setHours(to.getHours() + 24, 0, 0, 0);

  return {
    fdesde: `${formatApiDatePart(from)} ${PAD2(from.getHours())}:00`,
    fhasta: `${formatApiDatePart(to)} ${PAD2(to.getHours())}:00`,
  };
}

function buildCalendarDayRange(fechaISO) {
  const [y, m, d] = String(fechaISO || isoToday())
    .split("-")
    .map(Number);
  const from = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0);
  const to = new Date(y, (m || 1) - 1, d || 1, 23, 59, 0);

  return {
    fdesde: `${formatApiDatePart(from)} 00:00`,
    fhasta: `${formatApiDatePart(to)} 23:59`,
  };
}

function extractDateOnly(value) {
  const str = String(value || "");
  const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];

  const slashMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    const [, mm, dd, yyyy] = slashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }

  return "";
}

function extractHour(value) {
  const str = String(value || "");
  const isoMatch = str.match(/T(\d{2}:\d{2}:\d{2})/);
  if (isoMatch) return isoMatch[1];

  const hourMatch = str.match(/(\d{2}:\d{2})(?::(\d{2}))?/);
  if (hourMatch) {
    const base = hourMatch[1];
    const sec = hourMatch[2] || "00";
    return `${base}:${sec}`;
  }

  return "";
}

function normalizeCompareText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isCodigoColor(value) {
  const normalized = normalizeCompareText(value);
  return (
    normalized === "ROJO" || normalized === "AMARILLO" || normalized === "VERDE"
  );
}

function extractRowsFromPayload(payload) {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.responseFinalizados,
    payload?.responseServicios,
    payload?.responseProductividad,
    payload?.responseRows,
    payload?.data,
    payload?.items,
    payload?.rows,
    payload?.result,
    payload?.results,
    payload?.servicios,
    payload?.finalizados,
  ];

  for (const item of candidates) {
    if (Array.isArray(item)) return item;
  }

  return [];
}

function extractPayloadArray(payload, keys) {
  if (!payload || typeof payload !== "object") return [];

  for (const key of keys) {
    const value = payload?.[key];
    if (Array.isArray(value)) return value;
  }

  return [];
}

function formatHorarioFromDates(fechaArmado, fechaCerrado) {
  const desde = extractHour(fechaArmado);
  const hasta = extractHour(fechaCerrado);
  if (!desde && !hasta) return "";
  if (desde && hasta) return `${desde.slice(0, 5)} a ${hasta.slice(0, 5)}`;
  if (desde) return `${desde.slice(0, 5)} a --:--`;
  return `--:-- a ${hasta.slice(0, 5)}`;
}

function buildHorarioMeta(fechaArmado, fechaCerrado) {
  const desde = fechaArmado ? String(fechaArmado) : "";
  const hasta = fechaCerrado ? String(fechaCerrado) : "";
  const horario = formatHorarioFromDates(desde, hasta);
  const desdeDate = parseAnyDate(desde);
  const hastaDate = parseAnyDate(hasta);

  return {
    desde,
    hasta,
    horario,
    desdeDate,
    hastaDate,
  };
}

function isReasonableOperationalDiffMs(diffMs) {
  return Number.isFinite(diffMs) && diffMs > 0 && diffMs <= 36 * 60 * 60 * 1000;
}

function chooseBestHorarioMetaForRow(spans, row) {
  if (!Array.isArray(spans) || !spans.length) return null;

  const servicioDesde = parseAnyDate(row?.servicioDesde);
  const servicioHasta = parseAnyDate(row?.servicioHasta);

  const razonables = spans.filter((meta) => {
    const desdeDate = meta?.desdeDate;
    const hastaDate = meta?.hastaDate;
    if (!desdeDate || !hastaDate) return false;
    return isReasonableOperationalDiffMs(
      hastaDate.getTime() - desdeDate.getTime(),
    );
  });

  if (!razonables.length) return null;

  const contieneServicio = razonables.find((meta) => {
    const desdeTime = meta.desdeDate.getTime();
    const hastaTime = meta.hastaDate.getTime();
    const servicioDesdeTime = servicioDesde ? servicioDesde.getTime() : null;
    const servicioHastaTime = servicioHasta ? servicioHasta.getTime() : null;

    const desdeDentro =
      servicioDesdeTime !== null &&
      servicioDesdeTime >= desdeTime &&
      servicioDesdeTime <= hastaTime;

    const hastaDentro =
      servicioHastaTime !== null &&
      servicioHastaTime >= desdeTime &&
      servicioHastaTime <= hastaTime;

    return desdeDentro || hastaDentro;
  });

  if (contieneServicio) return contieneServicio;

  if (servicioDesde) {
    const servicioDesdeTime = servicioDesde.getTime();
    return razonables.reduce((best, meta) => {
      if (!best) return meta;

      const bestDistance = Math.abs(
        best.desdeDate.getTime() - servicioDesdeTime,
      );
      const metaDistance = Math.abs(
        meta.desdeDate.getTime() - servicioDesdeTime,
      );

      if (metaDistance !== bestDistance) {
        return metaDistance < bestDistance ? meta : best;
      }

      const bestDiff = best.hastaDate.getTime() - best.desdeDate.getTime();
      const metaDiff = meta.hastaDate.getTime() - meta.desdeDate.getTime();
      return metaDiff < bestDiff ? meta : best;
    }, null);
  }

  return razonables.reduce((best, meta) => {
    if (!best) return meta;
    const bestDiff = best.hastaDate.getTime() - best.desdeDate.getTime();
    const metaDiff = meta.hastaDate.getTime() - meta.desdeDate.getTime();
    return metaDiff < bestDiff ? meta : best;
  }, null);
}

function buildTripulacionHorarioMaps(items) {
  const byTripulacionId = new Map();
  const byMovilSpans = new Map();

  for (const item of items || []) {
    const tripId = String(
      pickFirst(item?.id, item?.tripulacionId, item?.tripulacionid) || "",
    ).trim();
    const movilId = String(
      pickFirst(item?.movilId, item?.movilid, item?.moviId, item?.moviid) || "",
    ).trim();
    const fechaArmado = pickFirst(
      item?.fechaArmado,
      item?.fArmado,
      item?.armado,
    );
    const fechaCerrado = pickFirst(
      item?.fechaCerrado,
      item?.fCerrado,
      item?.cerradoFecha,
      item?.fechaFin,
    );
    const meta = buildHorarioMeta(fechaArmado, fechaCerrado);

    if (!meta.horario || !meta.desdeDate || !meta.hastaDate) continue;

    const diffMs = meta.hastaDate.getTime() - meta.desdeDate.getTime();
    if (!isReasonableOperationalDiffMs(diffMs)) continue;

    if (tripId) {
      byTripulacionId.set(tripId, meta);
    }

    if (movilId) {
      const current = byMovilSpans.get(movilId) || [];
      current.push(meta);
      byMovilSpans.set(movilId, current);
    }
  }

  return { byTripulacionId, byMovilSpans };
}

function applyTripulacionHorarios(rows, tripulaciones) {
  const { byTripulacionId, byMovilSpans } =
    buildTripulacionHorarioMaps(tripulaciones);

  return (rows || []).map((row) => {
    const tripId = String(row.tripulacionId || "").trim();
    const movilId = String(row.movil || "").trim();
    const horarioMeta = byTripulacionId.get(tripId) ||
      chooseBestHorarioMetaForRow(byMovilSpans.get(movilId), row) || {
        horario: row.horarioOperativo || "",
        desde: row.horarioOperativoDesde || "",
        hasta: row.horarioOperativoHasta || "",
      };

    return normalizeRow({
      ...row,
      horarioOperativo: horarioMeta.horario || row.horarioOperativo || "",
      horarioOperativoDesde:
        horarioMeta.desde || row.horarioOperativoDesde || "",
      horarioOperativoHasta:
        horarioMeta.hasta || row.horarioOperativoHasta || "",
    });
  });
}

function normalizeDerivacionEdad(rawValue, edadNumerica) {
  const edadMatch = String(edadNumerica ?? "")
    .replace(",", ".")
    .match(/-?\d+(?:\.\d+)?/);
  const edad = edadMatch ? Number(edadMatch[0]) : Number.NaN;

  if (!Number.isNaN(edad) && edad >= 0) {
    return edad > 13 ? "Adultos" : "Pediátricos";
  }

  const txt = normalizeCompareText(rawValue);
  if (txt.includes("PED")) return "Pediátricos";
  if (txt.includes("ADUL")) return "Adultos";

  return "";
}

function normalizeTrasladoTipo(rawValue) {
  const txt = normalizeCompareText(rawValue);
  if (!txt) return "";
  if (txt.includes("UTIM")) return "UTIM";
  if (txt.includes("UTIP")) return "UTIP";
  if (txt.includes("CON") && txt.includes("MED")) return "CON_MEDICO";
  if (txt.includes("SIN") && txt.includes("MED")) return "SIN_MEDICO";
  return txt;
}

function hasDerivacionData(item) {
  const derivedCandidates = [
    item?.derivado,
    item?.Derivado,
    item?.detalleDerivado,
    item?.DetalleDerivado,
    item?.derivacion,
    item?.derivaciones,
    item?.cantidadDerivaciones,
    item?.cantidadderivaciones,
    item?.cantidad_derivaciones,
  ];

  return derivedCandidates.some((value) => {
    if (value === undefined || value === null) return false;
    if (typeof value === "number") return Number.isFinite(value) && value > 0;
    return String(value).trim() !== "";
  });
}

function inferTrasladoFromMotivo(rawValue) {
  const txt = normalizeCompareText(rawValue);
  if (!txt) return false;
  return txt.includes("TRASLADO");
}

function parseFlexibleBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = normalizeCompareText(value);
  return (
    normalized === "TRUE" ||
    normalized === "1" ||
    normalized === "SI" ||
    normalized === "S" ||
    normalized === "YES"
  );
}

function firstTrasladoPayload(item) {
  const direct = pickFirst(
    item?.traslados,
    item?.trasladoData,
    item?.trasladoDetalle,
  );

  if (Array.isArray(direct)) {
    return direct.find((entry) => entry && typeof entry === "object") || null;
  }

  if (direct && typeof direct === "object") {
    return direct;
  }

  return null;
}

function extractTrasladoMeta(item) {
  const trasladoPayload = firstTrasladoPayload(item);
  const trasladoTipoRaw = pickFirst(
    trasladoPayload?.tiposMoviles?.nombre,
    trasladoPayload?.tiposMoviles?.descripcion,
    trasladoPayload?.tipoMovil?.nombre,
    trasladoPayload?.tipoMovil?.descripcion,
    trasladoPayload?.tipoMovilNombre,
    trasladoPayload?.tipo_movil_nombre,
    trasladoPayload?.descripcion,
    trasladoPayload?.nombre,
    item?.trasladoTipo,
    item?.tipoTraslado,
    item?.tipo_traslado,
    item?.complejidad,
    item?.Complejidad,
  );

  const trasladoTipo = normalizeTrasladoTipo(trasladoTipoRaw);
  const trasladoBaseRaw = pickFirst(
    item?.baseNombre,
    item?.base,
    item?.tripulaciones?.bases?.nombre,
    item?.tripulacion?.bases?.nombre,
    item?.tripulaciones?.base?.nombre,
    item?.tripulacion?.base?.nombre,
  );

  const esTraslado =
    parseFlexibleBoolean(
      pickFirst(
        item?.traslado,
        item?.Traslado,
        item?.esTraslado,
        item?.isTraslado,
        item?.trasladoServicio,
      ),
    ) ||
    Boolean(trasladoPayload) ||
    Boolean(trasladoTipo) ||
    isTrasladoBaseName(trasladoBaseRaw) ||
    inferTrasladoFromMotivo(
      pickFirst(item?.motivoConsulta, item?.MotivoConsulta),
    ) ||
    normalizeTipoServicio(
      pickFirst(
        item?.tipo,
        item?.tiposervicio,
        item?.servicioTipo,
        item?.tipoServicio,
        item?.tipo_servicio,
      ),
    ) === "TRASLADO";

  return {
    esTraslado,
    trasladoTipo,
    trasladoPayload,
  };
}

function normalizeTipoServicio(rawValue) {
  const txt = normalizeCompareText(rawValue);
  if (!txt) return "";
  if (txt.includes("TRAS")) return "TRASLADO";
  if (txt.includes("DERIV")) return "DERIVACION";
  if (txt.includes("EVENT")) return "EVENTO";
  if (txt.includes("ANUL")) return "ANULADO";
  return txt;
}

function resolveOptionLabel(value, options) {
  const normalized = normalizeCompareText(value);
  if (!normalized) return "";
  const found = options.find(
    (option) => normalizeCompareText(option) === normalized,
  );
  return found || "";
}

function resolveAliasLabel(value, config) {
  const normalized = normalizeCompareText(value);
  if (!normalized) return "";

  const compact = normalized.replace(/\s+/g, "");

  for (const entry of config || []) {
    for (const alias of entry.aliases || []) {
      const aliasNormalized = normalizeCompareText(alias);
      if (!aliasNormalized) continue;

      const aliasCompact = aliasNormalized.replace(/\s+/g, "");

      if (
        normalized === aliasNormalized ||
        normalized.includes(aliasNormalized) ||
        aliasNormalized.includes(normalized) ||
        compact === aliasCompact ||
        compact.includes(aliasCompact) ||
        aliasCompact.includes(compact)
      ) {
        return entry.label;
      }
    }
  }

  return "";
}

function resolvePrestadorOperativoLabel(rawValue) {
  return (
    resolveAliasLabel(rawValue, PRESTADOR_ALIAS_CONFIG) ||
    resolveOptionLabel(rawValue, [
      ...PRESTADORES_ZONA_MEDIC,
      ...PRESTADORES_FUERA_ZONA,
    ]) ||
    ""
  );
}

function resolveTitularMeta(rawValue, titularId, grupoId, grupoNombre) {
  const titularIdText = String(titularId || "").trim();
  const grupoIdText = String(grupoId || "").trim();
  const grupoNombreText = normalizeCompareText(grupoNombre);
  const rawText = String(rawValue || "").trim();

  const socioDetectado =
    resolveAliasLabel(rawText, SOCIO_ALIAS_CONFIG) ||
    resolveOptionLabel(rawText, SOCIOS_TRASLADO) ||
    "";

  const prestador = resolvePrestadorOperativoLabel(rawText);

  const esSocioDirectoPorGrupo =
    grupoIdText === "1" ||
    grupoIdText === "2" ||
    grupoNombreText.includes("SOCIO DIRECTO") ||
    grupoNombreText.includes("SOCIOS DIRECTOS");

  const esConvenioPorGrupo =
    grupoIdText === "5" || grupoNombreText.includes("CONVENIO");

  const socio = socioDetectado || (rawText ? "Socio directo" : "");

  if (esSocioDirectoPorGrupo) {
    return {
      convenioLabel: "Socio directo",
      socio: "Socio directo",
      prestador: "",
      titularNumero: titularIdText,
      grupoId: grupoIdText,
      grupoNombre: String(grupoNombre || "").trim(),
    };
  }

  if (esConvenioPorGrupo) {
    return {
      convenioLabel: socio || prestador || rawText,
      socio,
      prestador,
      titularNumero: titularIdText,
      grupoId: grupoIdText,
      grupoNombre: String(grupoNombre || "").trim(),
    };
  }

  return {
    convenioLabel: socio || prestador || rawText,
    socio,
    prestador,
    titularNumero: titularIdText,
    grupoId: grupoIdText,
    grupoNombre: String(grupoNombre || "").trim(),
  };
}

function resolveSocioFromTrasladoBase(baseValue) {
  const normalizedBase = normalizeCompareText(baseValue);
  if (!normalizedBase) return "";
  if (normalizedBase.includes("PAMI TRASLADOS")) return "PAMI";
  if (normalizedBase.includes("SAME TRASLADOS")) return "SAME";
  return "";
}

function isTrasladoBaseName(baseValue) {
  const normalizedBase = normalizeCompareText(baseValue);
  if (!normalizedBase) return false;
  return (
    normalizedBase.includes("PAMI TRASLADOS") ||
    normalizedBase.includes("SAME TRASLADOS")
  );
}

function resolveSocioFromRow(row) {
  const titular = resolveTitularMeta(
    row?.nombreTitular,
    row?.titularId,
    row?.grupoId,
    row?.grupoNombre,
  );
  const convenioRaw = pickFirst(
    row?.convenio,
    row?.obraSocialConvenio,
    row?.socioTraslado,
  );
  const convenioSocio =
    resolveAliasLabel(convenioRaw, SOCIO_ALIAS_CONFIG) ||
    resolveOptionLabel(convenioRaw, SOCIOS_TRASLADO) ||
    "";

  const trasladoBaseSocio =
    row?.esTraslado === true ||
    normalizeTipoServicio(row?.tipo) === "TRASLADO" ||
    Boolean(normalizeTrasladoTipo(row?.trasladoTipo)) ||
    normalizeColorValue(row?.color) === "AZUL" ||
    normalizeColorValue(row?.color) === "CELESTE" ||
    isTrasladoBaseName(row?.base)
      ? resolveSocioFromTrasladoBase(row?.base)
      : "";

  return trasladoBaseSocio || convenioSocio || titular.socio || "";
}

function getPrestadorRawCandidate(row) {
  return String(
    pickFirst(
      row?.prestadorRaw,
      row?.prestador,
      row?.prestadorMovil,
      row?.prestadorDesdeMovil,
    ) || "",
  ).trim();
}

function hasPrestadorSinIdentificarData(row) {
  const raw = getPrestadorRawCandidate(row);
  const normalized = normalizeCompareText(raw);
  if (!normalized) return false;
  if (resolvePrestadorOperativoLabel(raw)) return false;
  if (/^MOVIL\s*\d+$/i.test(raw)) return false;
  if (normalized === "AUSENTE") return false;
  if (normalized === "NINGUNO") return false;
  if (normalized === "SIN PRESTADOR") return false;
  return true;
}

function resolvePrestadorFromRow(row) {
  const prestadorDesdeCamposDirectos = resolvePrestadorOperativoLabel(
    pickFirst(
      row?.prestador,
      row?.prestadorMovil,
      row?.prestadorDesdeMovil,
      row?.movilesNombre,
      row?.medico,
      row?.medicoNombre,
    ),
  );
  if (prestadorDesdeCamposDirectos) return prestadorDesdeCamposDirectos;

  // Caso especial pedido: cuando la base operativa viene como PRESTADOR,
  // el servicio corresponde a REALIZADOS POR PRESTADOR en la fila Dr. Carrion,
  // pero no debe sumarse ni mostrarse en la planilla POR MÓVIL.
  const prestadorDesdeBase = resolvePrestadorOperativoLabel(row?.base);
  if (prestadorDesdeBase) return prestadorDesdeBase;

  if (hasPrestadorSinIdentificarData(row)) {
    return PRESTADOR_SIN_IDENTIFICAR_LABEL;
  }

  return "";
}

function isBlackRow(row) {
  return normalizeCompareText(row?.color) === "NEGRO";
}

function isEventoOperativoRow(row) {
  return normalizeCompareText(row?.color) === "NARANJA";
}

function isAnuladoRow(row) {
  const estado = normalizeTipoServicio(row?.estado);
  const tipo = normalizeTipoServicio(row?.tipo);
  return (
    row?.esAnulado === true ||
    estado === "ANULADO" ||
    tipo === "ANULADO" ||
    Boolean(String(row?.anulacionRaw || "").trim())
  );
}

function isTrasladoOperativoRow(row) {
  if (isBlackRow(row)) return false;
  if (isEventoOperativoRow(row)) return false;

  const color = normalizeColorValue(row?.color);

  return (
    row?.esTraslado === true ||
    normalizeTipoServicio(row?.tipo) === "TRASLADO" ||
    Boolean(normalizeTrasladoTipo(row?.trasladoTipo)) ||
    isTrasladoBaseName(row?.base) ||
    color === "AZUL" ||
    color === "CELESTE"
  );
}

function isProductividadTrasladoPamiUtimRow(row) {
  const color = normalizeColorValue(row?.color);
  if (color !== "CELESTE" && color !== "AZUL") return false;
  if (resolveSocioFromRow(row) !== "PAMI") return false;
  if (normalizeTrasladoTipo(row?.trasladoTipo) !== "UTIM") return false;
  return isTrasladoOperativoRow(row);
}

function isProductividadServicioMovilRow(row) {
  if (!row) return false;
  if (isBlackRow(row) || isInoperanteRow(row)) return false;
  if (isEventoOperativoRow(row)) return false;
  if (isTrasladoOperativoRow(row)) return false;

  const base = resolveOperativeBaseLabel(row?.base);
  const movil = String(row?.movil || "").trim();
  if (!base || !movil) return false;

  // Estas bases no deben aparecer ni sumar en POR MÓVIL.
  // PRESTADOR se manda a REALIZADOS POR PRESTADOR / Dr. Carrion.
  // BANCO, PAMI TRASLADOS y SAME TRASLADOS quedan fuera de esta cartilla.
  if (isExcludedPorMovilBase(base)) return false;

  if (resolvePrestadorFromRow(row)) return false;

  return true;
}

function isTrasladoTablaRow(row) {
  if (!row || isBlackRow(row) || isAnuladoRow(row)) return false;
  const estado = normalizeCompareText(row?.estado);
  if (estado && estado !== "FINALIZADO" && estado !== "DESPACHADO") return false;
  return (
    row?.hasTrasladoPayload === true ||
    row?.esTrasladoEstricto === true ||
    normalizeTipoServicio(row?.tipo) === "TRASLADO"
  );
}

function resolveTrasladoTipoForTable(row) {
  const trasladoTipo = normalizeTrasladoTipo(row?.trasladoTipo);
  if (trasladoTipo) return trasladoTipo;

  if (row?.hasTrasladoPayload === true) {
    const color = normalizeColorValue(row?.color);
    if (color === "CELESTE") return "UTIM";
    return "SIN_MEDICO";
  }

  return "";
}

function isPrestadorOperativoRow(row) {
  if (isBlackRow(row)) return false;
  return Boolean(resolvePrestadorFromRow(row));
}

function isInoperanteRow(row) {
  return normalizeCompareText(row?.nombreTitular) === "INOPERANTE";
}

function parseHorarioRango(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{1,2}:\d{2})\s*a\s*(\d{1,2}:\d{2})$/i);
  if (!match) return null;

  const [, desde, hasta] = match;
  return {
    desde,
    hasta,
    desdeMin: parseHHMM(desde),
    hastaMin: parseHHMM(hasta),
  };
}

function getHorarioSplitMinutes(value) {
  const rango = parseHorarioRango(value);
  if (!rango) return null;

  const primerTramoInicio = rango.desdeMin;
  const primerTramoFin = (rango.desdeMin + 12 * 60) % (24 * 60);

  return {
    ...rango,
    primerTramoInicio,
    primerTramoFin,
  };
}

function getHorarioDurationMinutes(value, desdeRaw = "", hastaRaw = "") {
  const desdeDate = parseAnyDate(desdeRaw);
  const hastaDate = parseAnyDate(hastaRaw);

  if (desdeDate && hastaDate) {
    const diffMs = hastaDate.getTime() - desdeDate.getTime();
    if (isReasonableOperationalDiffMs(diffMs)) {
      return Math.round(diffMs / 60000);
    }
  }

  const rango = parseHorarioRango(value);
  if (!rango) return 0;

  if (rango.desdeMin === rango.hastaMin) return 24 * 60;
  if (rango.hastaMin > rango.desdeMin) return rango.hastaMin - rango.desdeMin;
  return 24 * 60 - rango.desdeMin + rango.hastaMin;
}

function formatMinutesAsHourLabel(totalMinutes) {
  const minutes = Number(totalMinutes || 0);
  if (!Number.isFinite(minutes) || minutes <= 0) return "";

  const safe = Math.max(0, Math.round(minutes));
  const fixedHours = Math.floor(safe / 60);
  return String(fixedHours);
}

function formatMinutesAsClockLabel(totalMinutes) {
  const minutes = Number(totalMinutes || 0);
  if (!Number.isFinite(minutes) || minutes <= 0) return "00:00";

  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${PAD2(hours)}:${PAD2(mins)}`;
}

function formatAverageAttentionLabel(totalMinutes) {
  const minutes = Number(totalMinutes || 0);
  if (!Number.isFinite(minutes) || minutes <= 0) return "";

  const safe = Math.max(0, Math.round(minutes));
  if (safe < 60) return `${safe} min`;

  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${PAD2(hours)}:${PAD2(mins)} hs`;
}

function parseHoursLabelToMinutes(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return null;

  const hhmmMatch = raw.match(/^(\d{1,3})\s*:\s*(\d{1,2})/);
  if (hhmmMatch) {
    const hours = Number(hhmmMatch[1]);
    const minutes = Number(hhmmMatch[2]);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return Math.max(0, Math.round(hours * 60 + minutes));
    }
  }

  const numberMatch = raw.replace(",", ".").match(/\d+(?:\.\d+)?/);
  if (!numberMatch) return null;

  const hours = Number(numberMatch[0]);
  if (!Number.isFinite(hours)) return null;
  return Math.max(0, Math.round(hours * 60));
}

function parseAverageAttentionLabelToMinutes(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return null;

  const hhmmMatch = raw.match(/^(\d{1,3})\s*:\s*(\d{1,2})/);
  if (hhmmMatch) {
    const hours = Number(hhmmMatch[1]);
    const minutes = Number(hhmmMatch[2]);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return Math.max(0, Math.round(hours * 60 + minutes));
    }
  }

  const numberMatch = raw.replace(",", ".").match(/\d+(?:\.\d+)?/);
  if (!numberMatch) return null;

  const amount = Number(numberMatch[0]);
  if (!Number.isFinite(amount)) return null;

  if (raw.includes("hs") || raw.includes("hora")) {
    return Math.max(0, Math.round(amount * 60));
  }

  return Math.max(0, Math.round(amount));
}

function getServiceDurationMinutes(row) {
  const fromRaw = pickFirst(row?.servicioDesde, row?.ftomado);
  const toRaw = pickFirst(row?.servicioHasta, row?.ffin);
  const diff = computeDemoraMin(fromRaw, toRaw);
  const numeric = Number(diff);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

function getRowsDurationMinutes(rows) {
  return (rows || []).reduce((total, row) => total + getServiceDurationMinutes(row), 0);
}

function buildInoperantesObservacion(rows) {
  const unique = [];
  const seen = new Set();

  for (const row of rows || []) {
    const text = String(
      pickFirst(row?.observaciones, row?.incidente) || "",
    ).trim();

    if (!text) continue;

    const key = normalizeCompareText(text);
    if (!key || seen.has(key)) continue;

    seen.add(key);
    unique.push(text);
  }

  return unique.join(" · ");
}

function resolveConvenioForSheet(row) {
  return (
    resolveSocioFromRow(row) ||
    String(row?.convenio || "").trim() ||
    String(row?.nombreTitular || "").trim()
  );
}

const ANULADOS_CONVENIO_ALIAS_CONFIG = [
  {
    label: "same",
    aliases: ["SAME", "107- SAME- LA MATANZA", "107- SAME- TRES DE FEBRERO"],
  },
  { label: "pami", aliases: ["PAMI", "D.A.M.N.P Y P"] },
  {
    label: "emerger",
    aliases: [
      "EMERGER",
      "EMERGER EMERGENCIAS MEDICAS",
      "EMERGER  (SERVICIOS SAN COR SALUD)",
    ],
  },
  {
    label: "swiss",
    aliases: [
      "SWISS",
      "SWISS MEDICAL",
      "SWISS MEDICAL SA",
      "SWISS MEDICAL S.A.",
    ],
  },
  { label: "doctor", aliases: ["DOCTOR", "DOCTOR RED", "DOCTORED", "ROI S.A"] },
  { label: "colegio", aliases: ["COLEGIO", "COLEG"] },
  {
    label: "area",
    aliases: [
      "AREA",
      "AREAS PROTEGIDA",
      "AREA PROTEGIDA",
      "AREAS PROTEGIDAS",
      "AREAS PROTEGIDA",
    ],
  },
  {
    label: "vittal",
    aliases: [
      "VITTAL",
      "SOCORRO MEDICO PRIVADO S A (VITTAL)",
      "SOCORRO MEDICO PRIVADO SA (VITTAL)",
    ],
  },
  { label: "interzona", aliases: ["INTERZONA", "INTERZONAS"] },
  { label: "aguero", aliases: ["AGUERO", "CLINICA AGUERO"] },
  { label: "hominnis", aliases: ["HOMINNIS", "HOMINIS"] },
  { label: "galeno", aliases: ["GALENO"] },
  { label: "amta", aliases: ["AMTA", "A.M.T.A"] },
  { label: "uom", aliases: ["UOM"] },
  { label: "evento", aliases: ["EVENTO"] },
];

function isGenericAnuladoGroupName(value) {
  const normalized = normalizeCompareText(value);
  if (!normalized) return true;
  return (
    normalized === "CONVENIOS" ||
    normalized === "ABONADOS COMUNES" ||
    normalized === "ABONADOS" ||
    normalized === "SOCIO DIRECTO" ||
    normalized === "SOCIOS DIRECTOS" ||
    normalized === "SIN CONVENIO"
  );
}

function simplifyAnuladoConvenioLabel(value) {
  const normalized = normalizeCompareText(value);
  if (!normalized) return "";
  const aliased = resolveAliasLabel(value, ANULADOS_CONVENIO_ALIAS_CONFIG);
  if (aliased) return aliased;

  if (normalized === "SWISS MEDICAL") return "swiss";
  if (normalized === "DOCTOR RED") return "doctor";
  if (normalized === "AREAS PROTEGIDA") return "area";

  return String(value || "")
    .trim()
    .toLowerCase();
}

function resolveAnuladoConvenioForSheet(row) {
  const aliasSources = [
    row?.grupoNombre,
    row?.convenio,
    row?.nombreTitular,
    row?.observaciones,
    row?.incidente,
  ];

  for (const source of aliasSources) {
    const aliased = resolveAliasLabel(source, ANULADOS_CONVENIO_ALIAS_CONFIG);
    if (aliased) return aliased;
  }

  const socio = resolveSocioFromRow(row);
  if (socio) {
    const simplifiedSocio = simplifyAnuladoConvenioLabel(socio);
    if (simplifiedSocio) return simplifiedSocio;
  }

  const convenio = String(row?.convenio || "").trim();
  if (convenio && !isGenericAnuladoGroupName(convenio)) {
    const simplifiedConvenio = simplifyAnuladoConvenioLabel(convenio);
    if (simplifiedConvenio) return simplifiedConvenio;
  }

  const grupoNombre = String(row?.grupoNombre || "").trim();
  if (grupoNombre && !isGenericAnuladoGroupName(grupoNombre)) {
    const simplifiedGrupo = simplifyAnuladoConvenioLabel(grupoNombre);
    if (simplifiedGrupo) return simplifiedGrupo;
  }

  return "sin convenio";
}

function guessZonaMedic(prestador) {
  const normalized = normalizeCompareText(prestador);
  if (!normalized) return false;
  if (
    PRESTADORES_ZONA_MEDIC.some(
      (option) => normalizeCompareText(option) === normalized,
    )
  )
    return true;
  if (
    PRESTADORES_FUERA_ZONA.some(
      (option) => normalizeCompareText(option) === normalized,
    )
  )
    return false;
  return false;
}

function guessHorarioOperativo(base, currentHorario) {
  if (String(currentHorario || "").trim()) return currentHorario;
  const normalizedBase = normalizeCompareText(base);
  const found = BASE_HORARIOS_DEFAULT.find((item) =>
    normalizedBase.includes(item.match),
  );
  return found?.horario || "";
}

function resolveOperativeBaseLabel(base) {
  const alias = resolveAliasLabel(base, BASES_OPERATIVAS_CONFIG);
  if (alias) return alias;

  // Las bases de POR MÓVIL son dinámicas: si la API trae una base nueva,
  // se muestra con su nombre original, sin depender de una lista hardcodeada.
  return String(base || "").trim();
}

function isExcludedPorMovilBase(base) {
  const normalized = normalizeCompareText(base);
  if (!normalized) return true;

  return (
    normalized === "PRESTADOR" ||
    normalized === "BANCO" ||
    normalized.includes("PAMI TRASLADO") ||
    normalized.includes("SAME TRASLADO")
  );
}

function mergeRowData(baseRow, extraRow) {
  const merged = { ...baseRow };
  const keys = new Set([
    ...Object.keys(baseRow || {}),
    ...Object.keys(extraRow || {}),
  ]);

  keys.forEach((key) => {
    const extraValue = extraRow?.[key];

    if (typeof extraValue === "boolean") {
      merged[key] = extraValue;
      return;
    }

    if (typeof extraValue === "number") {
      if (!Number.isNaN(extraValue)) merged[key] = extraValue;
      return;
    }

    if (
      extraValue !== undefined &&
      extraValue !== null &&
      String(extraValue).trim() !== ""
    ) {
      merged[key] = extraValue;
    }
  });

  return normalizeRow(merged);
}

function mergeRowsById(baseRows, extraRows) {
  const map = new Map();

  for (const row of baseRows || []) {
    map.set(
      String(
        row.id || `${row.base || ""}-${row.movil || ""}-${row.hora || ""}`,
      ),
      normalizeRow(row),
    );
  }

  for (const row of extraRows || []) {
    const key = String(
      row.id || `${row.base || ""}-${row.movil || ""}-${row.hora || ""}`,
    );
    if (!map.has(key)) {
      map.set(key, normalizeRow(row));
    } else {
      map.set(key, mergeRowData(map.get(key), row));
    }
  }

  const out = Array.from(map.values());
  out.sort((a, b) => {
    const aKey = `${a.fechaISO || ""} ${a.hora || ""} ${a.id || ""}`;
    const bKey = `${b.fechaISO || ""} ${b.hora || ""} ${b.id || ""}`;
    return aKey.localeCompare(bKey);
  });
  return out;
}

function normalizeRow(r) {
  return {
    id: r?.id ?? `${r?.fechaISO ?? "x"}-${Math.random().toString(16).slice(2)}`,
    fechaISO: r?.fechaISO ?? "",
    hora: r?.hora ?? "",
    horaFin: r?.horaFin ?? "",
    base: r?.base ?? "",
    baseId: r?.baseId ?? "",
    movil: r?.movil ?? "",
    tripulacionId: r?.tripulacionId ?? "",
    color: normalizeColorValue(r?.color),
    estado: r?.estado ?? "",
    tipo: r?.tipo ?? "",
    convenio: r?.convenio ?? "",
    obraSocialConvenio: r?.obraSocialConvenio ?? "",
    localidad: r?.localidad ?? "",
    operador: r?.operador ?? "",
    incidente: r?.incidente ?? "",
    demoraMin: r?.demoraMin ?? "",
    horarioAnulacion: r?.horarioAnulacion ?? "",
    observaciones: r?.observaciones ?? "",
    horarioOperativo: r?.horarioOperativo ?? "",
    horarioOperativoDesde: r?.horarioOperativoDesde ?? "",
    horarioOperativoHasta: r?.horarioOperativoHasta ?? "",
    prestador: r?.prestador ?? "",
    prestadorMovil: r?.prestadorMovil ?? "",
    prestadorRaw: r?.prestadorRaw ?? "",
    zonaMedic: typeof r?.zonaMedic === "boolean" ? r.zonaMedic : undefined,
    socioTraslado: r?.socioTraslado ?? "",
    esTraslado: typeof r?.esTraslado === "boolean" ? r.esTraslado : false,
    esTrasladoEstricto:
      typeof r?.esTrasladoEstricto === "boolean" ? r.esTrasladoEstricto : false,
    hasTrasladoPayload:
      typeof r?.hasTrasladoPayload === "boolean" ? r.hasTrasladoPayload : false,
    trasladoTipo: r?.trasladoTipo ?? "",
    derivacionEdad: r?.derivacionEdad ?? "",
    cantidadDerivaciones: Number(r?.cantidadDerivaciones ?? 0) || 0,
    servicioDesde: r?.servicioDesde ?? "",
    servicioHasta: r?.servicioHasta ?? "",
    titularId: r?.titularId ?? "",
    grupoId: r?.grupoId ?? "",
    grupoNombre: r?.grupoNombre ?? "",
    nombreTitular: r?.nombreTitular ?? "",
    esAnulado: typeof r?.esAnulado === "boolean" ? r.esAnulado : false,
    anulacionRaw: r?.anulacionRaw ?? "",
  };
}

function mapAnuladoApiRow(item) {
  const row = mapServicioApiRow(item);
  const tomado = pickFirst(
    item?.ftomado,
    item?.fTomado,
    item?.fechaTomado,
    item?.fdesdeServicio,
    row.servicioDesde,
  );
  const anulado = pickFirst(
    item?.fanulado,
    item?.fAnulado,
    item?.horarioAnulacion,
    item?.fechaAnulacion,
    item?.fechaCerrado,
    row.servicioHasta,
  );
  const demoraCalculada = computeDemoraMin(tomado, anulado);

  return normalizeRow({
    ...row,
    estado: "ANULADO",
    tipo: "ANULADO",
    convenio: resolveAnuladoConvenioForSheet(row),
    incidente: pickFirst(item?.id, row.id, row.incidente),
    horarioAnulacion: extractHour(anulado) || row.horarioAnulacion || row.hora,
    observaciones:
      pickFirst(
        item?.motivoAnulado,
        item?.motivoAnulacion,
        item?.observaciones,
        item?.obs,
        item?.comentarios,
      ) || row.observaciones,
    demoraMin:
      pickFirst(
        item?.demoraMin,
        item?.demora,
        item?.minutosDemora,
        demoraCalculada,
      ) || row.demoraMin,
  });
}

function pickBestServicioBaseObject(item, tripulacion) {
  const candidates = [
    pickFirst(item?.bases, item?.base),
    pickFirst(tripulacion?.bases, tripulacion?.base),
  ].filter(Boolean);

  let fallback = {};

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;

    if (!fallback || !Object.keys(fallback).length) {
      fallback = candidate;
    }

    const candidateNombre = pickFirst(
      candidate?.nombre,
      candidate?.descripcion,
      candidate?.baseNombre,
      candidate?.base,
    );

    if (resolveOperativeBaseLabel(candidateNombre)) {
      return candidate;
    }
  }

  return fallback || {};
}

function mapServicioApiRow(item) {
  const tripulacion =
    pickFirst(item?.tripulaciones, item?.tripulacion, item?.movilData) || {};
  const hasTripulacion = Boolean(
    tripulacion && Object.keys(tripulacion).length,
  );
  const baseObj = pickBestServicioBaseObject(item, tripulacion);
  const movilObj = pickFirst(item?.moviles, item?.movilData, item?.movil) || {};
  const prestadorObj = pickFirst(item?.prestadores, item?.prestadorData) || {};
  const trasladoMeta = extractTrasladoMeta(item);

  const tomado = pickFirst(
    item?.ftomado,
    item?.fTomado,
    item?.fechaTomado,
    item?.fdesdeServicio,
  );
  const fin = pickFirst(
    item?.ffin,
    item?.fFin,
    item?.fechaFin,
    item?.fhastaServicio,
  );
  const anulacion = pickFirst(
    item?.fanulado,
    item?.fAnulado,
    item?.horarioAnulacion,
    item?.fechaAnulacion,
  );

  const baseNombre = pickFirst(
    baseObj?.nombre,
    baseObj?.descripcion,
    item?.baseNombre,
    item?.base,
    tripulacion?.bases?.nombre,
    tripulacion?.base?.nombre,
  );
  const prestadorDesdeMovilRaw = pickFirst(
    movilObj?.nombre,
    movilObj?.descripcion,
    item?.movilNombre,
    item?.movilesNombre,
  );
  const prestadorDesdeMovil = resolvePrestadorOperativoLabel(
    prestadorDesdeMovilRaw,
  );

  const titularId = String(
    pickFirst(
      item?.titularId,
      item?.titularID,
      item?.titularid,
      item?.titular?.id,
    ) || "",
  ).trim();
  const grupoId = String(
    pickFirst(
      item?.grupoId,
      item?.grupoID,
      item?.grupoid,
      item?.grupos?.id,
      item?.grupo?.id,
    ) || "",
  ).trim();
  const grupoNombre = pickFirst(
    item?.grupoNombre,
    item?.grupo_nombre,
    item?.grupos?.nombre,
    item?.grupo?.nombre,
  );
  const nombreTitular = pickFirst(
    item?.nombreTitular,
    item?.nombre_titular,
    item?.titularNombre,
    item?.titular?.nombre,
  );
  const titularMeta = resolveTitularMeta(
    nombreTitular,
    titularId,
    grupoId,
    grupoNombre,
  );

  const prestadorExplicitoRaw = pickFirst(
    prestadorObj?.nombre,
    item?.prestador,
    item?.prestadorNombre,
    item?.prestador_nombre,
    item?.nombrePrestador,
  );
  const prestadorExplicito = resolvePrestadorOperativoLabel(
    prestadorExplicitoRaw,
  );

  const prestadorNombre =
    (!hasTripulacion && prestadorDesdeMovil) || prestadorExplicito || "";

  const socioTraslado =
    titularMeta.socio ||
    resolveAliasLabel(
      pickFirst(
        item?.socioTraslado,
        item?.socio,
        item?.socioNombre,
        item?.socio_nombre,
        item?.convenio,
        item?.cobertura,
        item?.obraSocialConvenio,
        item?.ObraSocialConvenio,
        !hasTripulacion ? prestadorDesdeMovil : "",
      ),
      SOCIO_ALIAS_CONFIG,
    ) ||
    resolveOptionLabel(
      pickFirst(
        item?.socioTraslado,
        item?.socio,
        item?.socioNombre,
        item?.socio_nombre,
        item?.convenio,
        item?.cobertura,
        item?.obraSocialConvenio,
        item?.ObraSocialConvenio,
        !hasTripulacion ? prestadorDesdeMovil : "",
      ),
      SOCIOS_TRASLADO,
    );

  const edadRaw = pickFirst(
    item?.edad,
    item?.edadPaciente,
    item?.edad_paciente,
    item?.anios,
    item?.años,
  );

  const horarioOperativo = guessHorarioOperativo(
    baseNombre,
    pickFirst(
      tripulacion?.horarioOperativo,
      tripulacion?.horario,
      item?.horarioOperativo,
      item?.horario,
    ),
  );

  const tipoServicioNormalizado = normalizeTipoServicio(
    pickFirst(
      item?.tipo,
      item?.tiposervicio,
      item?.servicioTipo,
      item?.tipoServicio,
      item?.tipo_servicio,
    ),
  );

  const trasladoBooleanFlag = parseFlexibleBoolean(
    pickFirst(
      item?.traslado,
      item?.Traslado,
      item?.esTraslado,
      item?.isTraslado,
      item?.trasladoServicio,
    ),
  );
  const hasTrasladoPayload = Boolean(trasladoMeta.trasladoPayload);
  const esTrasladoEstricto =
    trasladoBooleanFlag ||
    hasTrasladoPayload ||
    tipoServicioNormalizado === "TRASLADO";
  const esTraslado = trasladoMeta.esTraslado;
  const trasladoTipoNormalizado =
    trasladoMeta.trasladoTipo ||
    normalizeTrasladoTipo(pickFirst(movilObj?.tipo, movilObj?.descripcion));

  return normalizeRow({
    id: pickFirst(item?.id, item?.servicioid, item?.servicioId),
    fechaISO: extractDateOnly(tomado || fin || anulacion),
    hora: extractHour(tomado),
    horaFin: extractHour(fin),
    base: baseNombre,
    baseId: pickFirst(baseObj?.id, item?.baseid, item?.baseId),
    movil: String(
      pickFirst(
        tripulacion?.moviId,
        tripulacion?.moviid,
        tripulacion?.movilid,
        tripulacion?.movilId,
        movilObj?.moviId,
        movilObj?.movilId,
        item?.moviId,
        item?.moviid,
        item?.movilid,
        item?.movilId,
        item?.movil,
      ) || "",
    ),
    tripulacionId: pickFirst(
      tripulacion?.id,
      item?.tripulacionid,
      item?.tripulacionId,
    ),
    color: toUpperText(
      pickFirst(item?.color, item?.codificacion, item?.prioridad),
    ),
    estado: toUpperText(pickFirst(item?.estado, "FINALIZADO")),
    tipo: tipoServicioNormalizado || (esTraslado ? "TRASLADO" : ""),
    convenio:
      pickFirst(
        item?.convenio,
        item?.cobertura,
        item?.financiador,
        item?.obraSocial,
        item?.obraSocialConvenio,
        item?.ObraSocialConvenio,
      ) ||
      titularMeta.convenioLabel ||
      String(nombreTitular || "").trim(),
    obraSocialConvenio: pickFirst(
      item?.obraSocialConvenio,
      item?.ObraSocialConvenio,
    ),
    localidad: pickFirst(item?.localidad, item?.destino, item?.lugar),
    operador: pickFirst(item?.operador, item?.usuario, item?.despachador),
    incidente: pickFirst(
      item?.incidente,
      item?.motivo,
      item?.detalle,
      item?.descripcion,
    ),
    demoraMin: pickFirst(item?.demoraMin, item?.demora, item?.minutosDemora),
    horarioAnulacion: extractHour(anulacion),
    observaciones: pickFirst(
      item?.observaciones,
      item?.obs,
      item?.comentarios,
      item?.motivoAnulado,
      item?.motivoAnulacion,
    ),
    horarioOperativo,
    prestador: prestadorNombre,
    prestadorMovil: !hasTripulacion ? prestadorDesdeMovil : "",
    prestadorRaw: pickFirst(
      prestadorExplicitoRaw,
      !hasTripulacion ? prestadorDesdeMovilRaw : "",
    ),
    zonaMedic: pickFirst(
      item?.zonaMedic,
      item?.enZonaMedic,
      item?.zona_medic,
      undefined,
    ),
    socioTraslado,
    esTraslado,
    esTrasladoEstricto,
    hasTrasladoPayload,
    trasladoTipo: trasladoTipoNormalizado,
    derivacionEdad: normalizeDerivacionEdad(
      pickFirst(item?.derivacionEdad, item?.edadDerivacion),
      edadRaw,
    ),
    cantidadDerivaciones: Number(
      pickFirst(
        item?.cantidadDerivaciones,
        item?.cantidadderivaciones,
        item?.cantidad_derivaciones,
        item?.cantidadDeDerivaciones,
        item?.cantidaddederivaciones,
        item?.cantDerivaciones,
        item?.cantidaddederivaciones,
        item?.derivaciones,
        item?.traslados?.cantidadDerivaciones,
        item?.traslados?.cantidadderivaciones,
        item?.traslado?.cantidadDerivaciones,
        item?.traslado?.cantidadderivaciones,
        hasDerivacionData(item) ? 1 : 0,
      ) || 0,
    ),
    servicioDesde: String(tomado || ""),
    servicioHasta: String(fin || ""),
    titularId,
    grupoId,
    grupoNombre: String(grupoNombre || "").trim(),
    nombreTitular: String(nombreTitular || "").trim(),
    esAnulado: Boolean(String(anulacion || "").trim()),
    anulacionRaw: String(anulacion || ""),
  });
}

export default function MitoDespachoScreen() {
  const [fechaISO, setFechaISO] = useState(clampFechaISO(isoToday()));
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [rows, setRows] = useState([]);
  const [productividadRows, setProductividadRows] = useState([]);
  const [anuladosRows, setAnuladosRows] = useState([]);
  const [trasladosRows, setTrasladosRows] = useState([]);
  const [tripulacionesRows, setTripulacionesRows] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastRange, setLastRange] = useState(
    buildMitoRange(clampFechaISO(isoToday())),
  );
  const [porMovilManual, setPorMovilManual] = useState(() =>
    getStoredPorMovilManual(),
  );
  const hourFormat = FIXED_HOUR_FORMAT;

  const [openProd, setOpenProd] = useState(false);
  const [openAnu, setOpenAnu] = useState(false);

  const inflightRef = useRef(false);

  const fetchData = async () => {
    if (inflightRef.current) return;

    inflightRef.current = true;
    setLoading(true);
    setErrorMsg("");

    try {
      const safeFechaISO = clampFechaISO(fechaISO);
      if (safeFechaISO !== fechaISO) {
        setFechaISO(safeFechaISO);
      }
      const range = buildMitoRange(safeFechaISO, operationalStartHour);
      setLastRange(range);

      const token = MITO_API_TOKEN || getStoredToken();
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const requestConfig = {
        method: "POST",
        headers,
        body: JSON.stringify(range),
      };

      const anuladosDayRequestConfig = {
        method: "POST",
        headers,
        body: JSON.stringify(buildCalendarDayRange(safeFechaISO)),
      };

      const [finalizadosResult, productividadResult, anuladosDayResult] =
        await Promise.allSettled([
          fetch(MITO_FINALIZADOS_ENDPOINT, requestConfig),
          fetch(MITO_PRODUCTIVIDAD_ENDPOINT, requestConfig),
          fetch(MITO_PRODUCTIVIDAD_ENDPOINT, anuladosDayRequestConfig),
        ]);

      let finalizadosRows = [];
      let productividadRowsApi = [];
      let anuladosRowsApi = [];
      let trasladosRowsApi = [];
      let tripulacionesRowsApi = [];
      let tripulacionesDayRowsApi = [];

      if (finalizadosResult.status === "fulfilled") {
        if (!finalizadosResult.value.ok) {
          throw new Error(
            `Error ${finalizadosResult.value.status} al consultar MITO finalizados`,
          );
        }
        const payload = await finalizadosResult.value.json();
        finalizadosRows =
          extractRowsFromPayload(payload).map(mapServicioApiRow);
      }

      if (productividadResult.status === "fulfilled") {
        if (!productividadResult.value.ok) {
          console.warn(
            `MITO productividad devolvió ${productividadResult.value.status}`,
          );
        } else {
          const payload = await productividadResult.value.json();
          productividadRowsApi = extractPayloadArray(payload, [
            "responseFinalizados",
            "responseServicios",
            "responseProductividad",
          ]).map(mapServicioApiRow);
          anuladosRowsApi = extractPayloadArray(payload, [
            "responseAnulados",
            "anulados",
          ]).map(mapAnuladoApiRow);
          tripulacionesRowsApi = extractPayloadArray(payload, [
            "responseTripulaciones",
            "tripulaciones",
          ]);
        }
      } else if (productividadResult.reason) {
        console.warn(
          "MITO productividad no disponible:",
          productividadResult.reason,
        );
      }

      if (anuladosDayResult.status === "fulfilled") {
        if (!anuladosDayResult.value.ok) {
          console.warn(
            `MITO anulados por día devolvió ${anuladosDayResult.value.status}`,
          );
        } else {
          const payload = await anuladosDayResult.value.json();
          const anuladosDayRows = extractPayloadArray(payload, [
            "responseAnulados",
            "anulados",
          ]).map(mapAnuladoApiRow);
          const trasladosDayRows = extractPayloadArray(payload, [
            "responseFinalizados",
            "responseServicios",
            "responseProductividad",
          ]).map(mapServicioApiRow);
          tripulacionesDayRowsApi = extractPayloadArray(payload, [
            "responseTripulaciones",
            "tripulaciones",
          ]);
          if (anuladosDayRows.length) {
            anuladosRowsApi = anuladosDayRows;
          }
          if (trasladosDayRows.length) {
            trasladosRowsApi = trasladosDayRows;
          }
        }
      } else if (anuladosDayResult.reason) {
        console.warn(
          "MITO anulados por día no disponible:",
          anuladosDayResult.reason,
        );
      }

      if (
        !finalizadosRows.length &&
        !productividadRowsApi.length &&
        !anuladosRowsApi.length
      ) {
        throw new Error("MITO no devolvió registros para el rango solicitado");
      }

      const mergedBase = mergeRowsById(finalizadosRows, productividadRowsApi);
      const mergedWithAnulados = mergeRowsById(mergedBase, anuladosRowsApi);
      const mergedProductividadRows = productividadRowsApi.length
        ? mergeRowsById(finalizadosRows, productividadRowsApi)
        : mergedBase;
      const rowsWithHorarios = applyTripulacionHorarios(
        mergedWithAnulados,
        tripulacionesRowsApi,
      );
      const productividadWithHorarios = applyTripulacionHorarios(
        mergedProductividadRows,
        tripulacionesRowsApi,
      );
      const anuladosWithHorarios = applyTripulacionHorarios(
        anuladosRowsApi,
        tripulacionesDayRowsApi.length
          ? tripulacionesDayRowsApi
          : tripulacionesRowsApi,
      );
      const trasladosWithHorarios = applyTripulacionHorarios(
        trasladosRowsApi,
        tripulacionesDayRowsApi.length
          ? tripulacionesDayRowsApi
          : tripulacionesRowsApi,
      );

      setRows(rowsWithHorarios);
      setProductividadRows(
        productividadWithHorarios.length
          ? productividadWithHorarios
          : rowsWithHorarios,
      );
      setAnuladosRows(anuladosWithHorarios);
      setTrasladosRows(trasladosWithHorarios);
      setTripulacionesRows(tripulacionesRowsApi);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("MITO fetch error:", error);
      setRows([]);
      setProductividadRows([]);
      setAnuladosRows([]);
      setTrasladosRows([]);
      setTripulacionesRows([]);
      setErrorMsg(
        error instanceof Error ? error.message : "No se pudo consultar MITO",
      );
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  };

  const operationalStartHour = String(FIXED_OPERATIONAL_START_HOUR);

  useEffect(() => {
    fetchData();
  }, [fechaISO, operationalStartHour]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => fetchData(), 8000);
    return () => clearInterval(id);
  }, [autoRefresh, fechaISO, operationalStartHour]);

  useEffect(() => {
    safeSetLocalStorage(
      POR_MOVIL_MANUAL_STORAGE_KEY,
      JSON.stringify(porMovilManual),
    );
  }, [porMovilManual]);

  const updatePorMovilManual = (row, field, value) => {
    const rowKey =
      row.manualKey || buildPorMovilManualKey(fechaISO, row.base, row.movil);

    setPorMovilManual((current) => ({
      ...current,
      [rowKey]: {
        ...(current[rowKey] || {}),
        [field]: value,
      },
    }));
  };

  const operationalRangeLabel = getOperationalRangeLabel(
    operationalStartHour,
    hourFormat,
  );

  const visibleRows = useMemo(() => rows, [rows]);

  const analysisRows = useMemo(() => {
    return productividadRows.length ? productividadRows : rows;
  }, [productividadRows, rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleRows;

    return visibleRows.filter((r) => {
      return (
        String(r.id || "")
          .toLowerCase()
          .includes(q) ||
        String(r.hora || "")
          .toLowerCase()
          .includes(q) ||
        String(r.horaFin || "")
          .toLowerCase()
          .includes(q) ||
        String(r.base || "")
          .toLowerCase()
          .includes(q) ||
        String(r.movil || "")
          .toLowerCase()
          .includes(q) ||
        String(r.estado || "")
          .toLowerCase()
          .includes(q) ||
        String(r.tipo || "")
          .toLowerCase()
          .includes(q) ||
        String(r.convenio || "")
          .toLowerCase()
          .includes(q) ||
        String(r.localidad || "")
          .toLowerCase()
          .includes(q) ||
        String(r.incidente || "")
          .toLowerCase()
          .includes(q) ||
        String(r.nombreTitular || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [visibleRows, query]);

  const anulados = useMemo(() => {
    const source = anuladosRows.length
      ? anuladosRows
      : rows.filter((r) => r.estado === "ANULADO");
    return source.filter((r) => !isBlackRow(r) && isCodigoColor(r.color));
  }, [anuladosRows, rows]);

  const anuladosSheetRows = useMemo(() => {
    return anulados.map((r, idx) => {
      const observaciones = String(r.observaciones || "").trim();
      const horarioAnulacion = String(r.horarioAnulacion || r.hora || "").slice(
        0,
        5,
      );
      const convenio = resolveAnuladoConvenioForSheet(r);

      return {
        key: r.id || String(idx),
        convenio,
        total: 1,
        rojo: normalizeCompareText(r.color) === "ROJO" ? 1 : 0,
        amarillo: normalizeCompareText(r.color) === "AMARILLO" ? 1 : 0,
        verde: normalizeCompareText(r.color) === "VERDE" ? 1 : 0,
        demora: r.demoraMin ?? "",
        demoraDisplay: formatDemoraDisplay(r.demoraMin),
        horarioAnulacion,
        incidente: String(r.id || r.incidente || "").trim(),
        observaciones,
        localidad: String(r.localidad || "").trim(),
        isDemora: normalizeCompareText(observaciones).includes("DEMORA"),
      };
    });
  }, [anulados]);

  const anuladosGroupedRows = useMemo(() => {
    const order = [];
    const grouped = new Map();

    for (const row of anuladosSheetRows) {
      const key = row.convenio || "";
      if (!grouped.has(key)) {
        grouped.set(key, []);
        order.push(key);
      }
      grouped.get(key).push(row);
    }

    return order.map((key) => ({
      convenio: key,
      total: (grouped.get(key) || []).length,
      items: grouped.get(key) || [],
    }));
  }, [anuladosSheetRows]);

  const anuladosTotals = useMemo(() => {
    const t = { total: 0, rojo: 0, amarillo: 0, verde: 0 };
    for (const r of anuladosSheetRows) {
      t.total += r.total || 0;
      t.rojo += r.rojo || 0;
      t.amarillo += r.amarillo || 0;
      t.verde += r.verde || 0;
    }
    return t;
  }, [anuladosSheetRows]);

  const anuladosPorHora = useMemo(() => {
    const buckets = [
      "00:00 a 06:00",
      "06:00 a 12:00",
      "12:00 a 18:00",
      "18:00 a 24:00",
    ];
    const base = {
      ROJO: Object.fromEntries(buckets.map((b) => [b, 0])),
      AMARILLO: Object.fromEntries(buckets.map((b) => [b, 0])),
      VERDE: Object.fromEntries(buckets.map((b) => [b, 0])),
    };

    for (const a of anulados) {
      const b = timeBucket(a.horarioAnulacion || a.hora);
      const colorKey = normalizeCompareText(a.color);
      if (colorKey === "ROJO") base.ROJO[b] += 1;
      if (colorKey === "AMARILLO") base.AMARILLO[b] += 1;
      if (colorKey === "VERDE") base.VERDE[b] += 1;
    }

    const row = (colorKey, label) => {
      const c = base[colorKey];
      const total = buckets.reduce((sum, k) => sum + (c[k] || 0), 0);
      return { label, ...c, Total: total };
    };

    const totalsRow = {
      label: "Totales:",
      ...Object.fromEntries(
        buckets.map((b) => [
          b,
          base.ROJO[b] + base.AMARILLO[b] + base.VERDE[b],
        ]),
      ),
      Total: buckets.reduce(
        (sum, b) => sum + (base.ROJO[b] + base.AMARILLO[b] + base.VERDE[b]),
        0,
      ),
    };

    return {
      buckets,
      rows: [
        row("ROJO", "Rojo"),
        row("AMARILLO", "Amarillo"),
        row("VERDE", "Verde"),
        totalsRow,
      ],
    };
  }, [anulados]);

  const { periodDesde, periodHasta } = useMemo(
    () => buildPeriodStrings(fechaISO, operationalStartHour),
    [fechaISO, operationalStartHour],
  );

  const isRealizado = (r) =>
    r.estado === "FINALIZADO" || r.estado === "DESPACHADO";

  const prestadorRealizadosRows = useMemo(() => {
    const prestadoresPermitidos = new Set(
      [
        ...PRESTADORES_ZONA_MEDIC,
        ...PRESTADORES_FUERA_ZONA,
        PRESTADOR_SIN_IDENTIFICAR_LABEL,
      ].map((prestador) => normalizeCompareText(prestador)),
    );

    return analysisRows.filter((r) => {
      if (!isRealizado(r) || isBlackRow(r)) return false;

      const prestador = resolvePrestadorFromRow(r);
      if (!prestador) return false;

      return prestadoresPermitidos.has(normalizeCompareText(prestador));
    });
  }, [analysisRows]);

  const productividadResumen = useMemo(() => {
    const totColor = { ROJO: 0, AMARILLO: 0, VERDE: 0, FUCSIA: 0, CELESTE: 0 };
    let traslados = 0;
    let eventos = 0;

    const derPorEdad = { Adultos: 0, Pediátricos: 0 };
    const derPorColor = { ROJO: 0, AMARILLO: 0, VERDE: 0, FUCSIA: 0 };

    const servicios = analysisRows.filter(
      (r) => r.estado !== "ANULADO" && !isBlackRow(r) && !isInoperanteRow(r),
    );
    const serviciosNoNegros = servicios;

    for (const r of serviciosNoNegros) {
      if (isEventoOperativoRow(r)) {
        eventos += 1;
        continue;
      }

      if (isProductividadTrasladoPamiUtimRow(r)) {
        traslados += 1;
        continue;
      }

      if (!isProductividadServicioMovilRow(r)) continue;

      const colorKey = normalizeColorValue(r?.color);
      if (colorKey && totColor[colorKey] !== undefined) {
        totColor[colorKey] += 1;
      }
    }

    for (const r of prestadorRealizadosRows) {
      if (derPorEdad[r.derivacionEdad] !== undefined)
        derPorEdad[r.derivacionEdad] += 1;
      if (derPorColor[r.color] !== undefined) derPorColor[r.color] += 1;
    }

    // Sumar los derivados a prestador por color dentro de Totales por color,
    // sin tocar la lógica de Por móvil, bases, traslados ni prestadores.
    totColor.ROJO += Number(derPorColor.ROJO || 0);
    totColor.AMARILLO += Number(derPorColor.AMARILLO || 0);
    totColor.VERDE += Number(derPorColor.VERDE || 0);
    totColor.FUCSIA += Number(derPorColor.FUCSIA || 0);

    const pamiRows = serviciosNoNegros.filter(
      (r) => resolveSocioFromRow(r) === "PAMI",
    );

    const pamiColor = { ROJO: 0, AMARILLO: 0, VERDE: 0, CELESTE: 0 };
    for (const r of pamiRows) {
      if (isProductividadTrasladoPamiUtimRow(r)) {
        pamiColor.CELESTE += 1;
        continue;
      }
      if (r.color !== "CELESTE" && r.color !== "AZUL" && pamiColor[r.color] !== undefined)
        pamiColor[r.color] += 1;
    }

    const totalServicios =
      Number(totColor.ROJO || 0) +
      Number(totColor.AMARILLO || 0) +
      Number(totColor.VERDE || 0) +
      Number(totColor.FUCSIA || 0) +
      Number(traslados || 0) +
      Number(eventos || 0);

    const totalPami =
      pamiColor.ROJO + pamiColor.AMARILLO + pamiColor.VERDE + pamiColor.CELESTE;
    const totalDerPorEdad =
      Number(derPorEdad.Adultos || 0) + Number(derPorEdad["Pediátricos"] || 0);
    const totalDerPorColor =
      Number(derPorColor.ROJO || 0) +
      Number(derPorColor.AMARILLO || 0) +
      Number(derPorColor.VERDE || 0) +
      Number(derPorColor.FUCSIA || 0);
    return {
      totColor,
      traslados,
      eventos,
      derPorEdad,
      derPorColor,
      totalServicios,
      pamiColor,
      totalPami,
      totalColorResumen: totalServicios,
      totalDerPorEdad,
      totalDerPorColor,
    };
  }, [analysisRows, prestadorRealizadosRows]);

  const porMovilGroups = useMemo(() => {
    const map = new Map();

    for (const r of analysisRows) {
      const rowInoperante = isInoperanteRow(r);
      if (isBlackRow(r) && !rowInoperante) continue;

      const base = resolveOperativeBaseLabel(r.base);
      const movil = String(r.movil || "").trim();

      if (!base || !movil) continue;
      if (isExcludedPorMovilBase(base)) continue;

      const key = base;
      if (!map.has(key)) {
        map.set(key, {
          key,
          base,
          moviles: new Set(),
          rows: [],
          subgroups: new Map(),
        });
      }

      const currentGroup = map.get(key);
      currentGroup.moviles.add(movil);
      currentGroup.rows.push({
        ...r,
        base,
        movil,
      });

      if (!currentGroup.subgroups.has(movil)) {
        currentGroup.subgroups.set(movil, {
          movil,
          horarioDefault: guessHorarioOperativo(base, r.horarioOperativo || ""),
          operativoDesde: "",
          operativoHasta: "",
          rows: [],
        });
      }

      const currentSubgroup = currentGroup.subgroups.get(movil);
      const guessedHorario = guessHorarioOperativo(base, r.horarioOperativo || "");
      if (!currentSubgroup.horarioDefault && guessedHorario) {
        currentSubgroup.horarioDefault = guessedHorario;
      }

      const desdeOperativo = String(r.horarioOperativoDesde || "").trim();
      const hastaOperativo = String(r.horarioOperativoHasta || "").trim();
      const desdeOperativoDate = parseAnyDate(desdeOperativo);
      const hastaOperativoDate = parseAnyDate(hastaOperativo);
      const currentDesdeDate = parseAnyDate(currentSubgroup.operativoDesde);
      const currentHastaDate = parseAnyDate(currentSubgroup.operativoHasta);

      if (desdeOperativo) {
        if (!currentSubgroup.operativoDesde) {
          currentSubgroup.operativoDesde = desdeOperativo;
        } else if (desdeOperativoDate && currentDesdeDate) {
          if (desdeOperativoDate.getTime() < currentDesdeDate.getTime()) {
            currentSubgroup.operativoDesde = desdeOperativo;
          }
        } else if (desdeOperativo < currentSubgroup.operativoDesde) {
          currentSubgroup.operativoDesde = desdeOperativo;
        }
      }

      if (hastaOperativo) {
        if (!currentSubgroup.operativoHasta) {
          currentSubgroup.operativoHasta = hastaOperativo;
        } else if (hastaOperativoDate && currentHastaDate) {
          if (hastaOperativoDate.getTime() > currentHastaDate.getTime()) {
            currentSubgroup.operativoHasta = hastaOperativo;
          }
        } else if (hastaOperativo > currentSubgroup.operativoHasta) {
          currentSubgroup.operativoHasta = hastaOperativo;
        }
      }

      currentSubgroup.rows.push({
        ...r,
        base,
        movil,
      });
    }

    return Array.from(map.values()).map((group) => ({
      ...group,
      moviles: Array.from(group.moviles).sort((a, b) => {
        const numA = Number(a);
        const numB = Number(b);
        if (Number.isFinite(numA) && Number.isFinite(numB) && numA !== numB) {
          return numA - numB;
        }
        return String(a).localeCompare(String(b));
      }),
      subgroups: Array.from(group.subgroups.values()).sort((a, b) => {
        const numA = Number(a.movil);
        const numB = Number(b.movil);
        if (Number.isFinite(numA) && Number.isFinite(numB) && numA !== numB) {
          return numA - numB;
        }
        return String(a.movil).localeCompare(String(b.movil));
      }),
    }));
  }, [analysisRows]);

  const analysisRowsByMovil = useMemo(() => {
    const map = new Map();

    for (const row of analysisRows) {
      const movil = String(row?.movil || "").trim();
      if (!movil) continue;
      const current = map.get(movil) || [];
      current.push(row);
      map.set(movil, current);
    }

    return map;
  }, [analysisRows]);

  const porMovilDisplayRows = useMemo(() => {
    return porMovilGroups
      .map((group) => {
        const movilLabel = group.moviles.join(", ");
        const rowKey = buildPorMovilManualKey(
          fechaISO,
          group.base,
          group.moviles.length === 1 ? group.moviles[0] : "__BASE_AGRUPADA__",
        );
        const manual = porMovilManual[rowKey] || {};
        const manualHorario = String(manual.horario ?? "").trim();
        const manualHorarioValido = Boolean(parseHorarioRango(manualHorario));
        const horariosDefault = [];
        const horariosDefaultSeen = new Set();

        let realizadosPrimerTramo = 0;
        let realizadosSegundoTramo = 0;
        let totalServicios = 0;
        let traslados = 0;
        let ingresados22a6 = 0;
        let realizados22a6 = 0;
        let inoperantes = 0;
        let horasOperativasMin = 0;
        let atencionDurationMin = 0;
        let atencionServicios = 0;
        const inoperantesRows = [];

        for (const subgroup of group.subgroups) {
          const defaultHorario = subgroup.horarioDefault || "";
          if (defaultHorario) {
            const horarioKey = normalizeCompareText(defaultHorario);
            if (horarioKey && !horariosDefaultSeen.has(horarioKey)) {
              horariosDefaultSeen.add(horarioKey);
              horariosDefault.push(defaultHorario);
            }
          }

          const horarioBase = manualHorarioValido ? manualHorario : defaultHorario;
          const horarioSplit = getHorarioSplitMinutes(horarioBase);
          const horasProgramadasMin = getHorarioDurationMinutes(
            horarioBase,
            subgroup.operativoDesde,
            subgroup.operativoHasta,
          );
          const subgroupInoperantesRows = [];

          for (const row of subgroup.rows) {
            const tomadoMin = parseHHMM(row.hora);
            const realizado = isRealizado(row);
            const inoperante = isInoperanteRow(row);
            const black = isBlackRow(row);
            const contabilizable = !black && !inoperante;
            const esTraslado = isTrasladoOperativoRow(row);
            const esTrasladoProductividad = isProductividadTrasladoPamiUtimRow(row);
            const esEvento = isEventoOperativoRow(row);

            if (inoperante) {
              inoperantes += 1;
              inoperantesRows.push(row);
              subgroupInoperantesRows.push(row);
              continue;
            }

            if (!contabilizable) continue;

            const cuentaComoServicioMovil = !esEvento && !esTraslado;

            if (cuentaComoServicioMovil && inRangeWrap(tomadoMin, 22 * 60, 6 * 60)) {
              ingresados22a6 += 1;
              if (realizado) realizados22a6 += 1;
            }

            if (realizado && cuentaComoServicioMovil) {
              totalServicios += 1;

              if (horarioSplit) {
                if (
                  inRangeWrap(
                    tomadoMin,
                    horarioSplit.primerTramoInicio,
                    horarioSplit.primerTramoFin,
                  )
                ) {
                  realizadosPrimerTramo += 1;
                } else {
                  realizadosSegundoTramo += 1;
                }
              }

              const serviceDurationMin = getServiceDurationMinutes(row);
              if (serviceDurationMin > 0) {
                atencionDurationMin += serviceDurationMin;
                atencionServicios += 1;
              }
            }

            if (realizado && esTrasladoProductividad) {
              traslados += 1;
            }
          }

          const inoperanteDurationMin = getRowsDurationMinutes(subgroupInoperantesRows);
          horasOperativasMin += Math.max(
            0,
            Number(horasProgramadasMin || 0) - Number(inoperanteDurationMin || 0),
          );
        }

        let derivaciones = 0;
        const derivacionesSeen = new Set();
        const rowsForDerivaciones = group.moviles.flatMap(
          (movil) => analysisRowsByMovil.get(String(movil || "").trim()) || [],
        );

        for (const row of rowsForDerivaciones) {
          const cantidadDerivaciones = Number(row?.cantidadDerivaciones || 0);
          if (
            !Number.isFinite(cantidadDerivaciones) ||
            cantidadDerivaciones <= 0 ||
            isEventoOperativoRow(row) ||
            isTrasladoOperativoRow(row)
          )
            continue;

          const derivacionKey = [
            String(row?.id || "").trim(),
            String(row?.tripulacionId || "").trim(),
            String(row?.servicioDesde || "").trim(),
            String(row?.servicioHasta || "").trim(),
          ].join("__");

          if (derivacionesSeen.has(derivacionKey)) continue;
          derivacionesSeen.add(derivacionKey);
          derivaciones += cantidadDerivaciones;
        }

        const horarioDefault = horariosDefault.join(" / ");
        const horario = manual.horario !== undefined ? manual.horario : horarioDefault;
        const promedioAtencionMin =
          atencionServicios > 0
            ? Math.round(atencionDurationMin / atencionServicios)
            : 0;
        const promedioAtencionAutoDisplay =
          formatAverageAttentionLabel(promedioAtencionMin);
        const horasOperativasDisplay =
          manual.horasOperativas ?? formatMinutesAsHourLabel(horasOperativasMin);
        const promedioAtencionDisplay =
          manual.promedioAtencion ?? promedioAtencionAutoDisplay;
        const horasOperativasManualMin = parseHoursLabelToMinutes(
          manual.horasOperativas,
        );
        const promedioAtencionManualMin = parseAverageAttentionLabelToMinutes(
          manual.promedioAtencion,
        );
        const observacionesInoperantesAuto =
          buildInoperantesObservacion(inoperantesRows);

        return {
          base: group.base,
          movil: movilLabel,
          manualKey: rowKey,
          horarioDefault,
          horario,
          operativoAuto: formatMinutesAsClockLabel(horasOperativasMin),
          operativoManual: manual.operativo ?? "",
          operativoDisplay:
            manual.operativo || formatMinutesAsClockLabel(horasOperativasMin),
          horasOperativasMin,
          horasOperativasMinForTotal:
            horasOperativasManualMin !== null
              ? horasOperativasManualMin
              : horasOperativasMin,
          horasOperativasDisplay,
          realizadosPrimerTramo,
          realizadosSegundoTramo,
          totalServicios,
          traslados,
          derivaciones,
          ingresados22a6,
          realizados22a6,
          promedioAtencionMin,
          promedioAtencionMinForTotal:
            promedioAtencionManualMin !== null
              ? promedioAtencionManualMin
              : promedioAtencionMin,
          promedioAtencionDisplay,
          promedioAtencionServicios: atencionServicios,
          promedioAtencionDurationMin: atencionDurationMin,
          inoperantes,
          observacionesInoperantes:
            manual.observacionesInoperantes ?? observacionesInoperantesAuto,
        };
      })
      .sort((a, b) => {
        const orderA =
          BASES_OPERATIVAS_ORDER.get(String(a.base || "")) ??
          Number.MAX_SAFE_INTEGER;
        const orderB =
          BASES_OPERATIVAS_ORDER.get(String(b.base || "")) ??
          Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;

        return String(a.base || "").localeCompare(String(b.base || ""));
      });
  }, [analysisRowsByMovil, fechaISO, porMovilGroups, porMovilManual]);

  const porMovilTotals = useMemo(() => {
    return porMovilDisplayRows.reduce(
      (acc, row) => {
        acc.horasOperativasMin += Number(
          row.horasOperativasMinForTotal ?? row.horasOperativasMin ?? 0,
        );
        acc.realizadosPrimerTramo += Number(row.realizadosPrimerTramo || 0);
        acc.realizadosSegundoTramo += Number(row.realizadosSegundoTramo || 0);
        acc.totalServicios += Number(row.totalServicios || 0);
        acc.traslados += Number(row.traslados || 0);
        acc.derivaciones += Number(row.derivaciones || 0);
        acc.ingresados22a6 += Number(row.ingresados22a6 || 0);
        acc.realizados22a6 += Number(row.realizados22a6 || 0);
        acc.promedioAtencionDurationMin +=
          Number(
            row.promedioAtencionMinForTotal ?? row.promedioAtencionMin ?? 0,
          ) * Number(row.promedioAtencionServicios || 0);
        acc.promedioAtencionServicios += Number(
          row.promedioAtencionServicios || 0,
        );
        acc.inoperantes += Number(row.inoperantes || 0);
        return acc;
      },
      {
        horasOperativasMin: 0,
        realizadosPrimerTramo: 0,
        realizadosSegundoTramo: 0,
        totalServicios: 0,
        traslados: 0,
        derivaciones: 0,
        ingresados22a6: 0,
        realizados22a6: 0,
        promedioAtencionDurationMin: 0,
        promedioAtencionServicios: 0,
        inoperantes: 0,
      },
    );
  }, [porMovilDisplayRows]);

  const porMovilHorasOperativasTotal = useMemo(
    () => formatMinutesAsHourLabel(porMovilTotals.horasOperativasMin),
    [porMovilTotals.horasOperativasMin],
  );

  const porMovilPromedioAtencionTotal = useMemo(() => {
    if (!Number(porMovilTotals.promedioAtencionServicios || 0)) return "";
    return formatMinutesAsClockLabel(
      Math.round(
        Number(porMovilTotals.promedioAtencionDurationMin || 0) /
          Number(porMovilTotals.promedioAtencionServicios || 1),
      ),
    );
  }, [
    porMovilTotals.promedioAtencionDurationMin,
    porMovilTotals.promedioAtencionServicios,
  ]);

  const prestadoresTabla = useMemo(() => {
    const zona = PRESTADORES_ZONA_MEDIC.map((p) => ({
      prestador: p,
      total: 0,
    }));
    const fuera = [
      ...PRESTADORES_FUERA_ZONA,
      PRESTADOR_SIN_IDENTIFICAR_LABEL,
    ].map((p) => ({
      prestador: p,
      total: 0,
    }));

    const mapZona = new Map(
      zona.map((r) => [normalizeCompareText(r.prestador), r]),
    );
    const mapFuera = new Map(
      fuera.map((r) => [normalizeCompareText(r.prestador), r]),
    );

    for (const r of prestadorRealizadosRows) {
      const prestador = resolvePrestadorFromRow(r);
      if (!prestador) continue;

      const prestadorKey = normalizeCompareText(prestador);
      const zonaMedic =
        typeof r.zonaMedic === "boolean"
          ? r.zonaMedic
          : guessZonaMedic(prestador);

      if (zonaMedic && mapZona.has(prestadorKey))
        mapZona.get(prestadorKey).total += 1;
      if (!zonaMedic && mapFuera.has(prestadorKey))
        mapFuera.get(prestadorKey).total += 1;
    }

    const subZona = zona.reduce((s, x) => s + x.total, 0);
    const subFuera = fuera.reduce((s, x) => s + x.total, 0);

    return { zona, fuera, subZona, subFuera, totalGeneral: subZona + subFuera };
  }, [prestadorRealizadosRows]);

  const trasladosTabla = useMemo(() => {
    const sourceRows = trasladosRows.length ? trasladosRows : analysisRows;
    const realizados = sourceRows.filter((r) => !isBlackRow(r));

    const init = (socio) => ({
      socio,
      UTIM: 0,
      UTIP: 0,
      CON_MEDICO: 0,
      SIN_MEDICO: 0,
      Total: 0,
    });

    const list = SOCIOS_TRASLADO.map(init);
    const map = new Map(list.map((r) => [normalizeCompareText(r.socio), r]));

    for (const r of realizados) {
      if (isEventoOperativoRow(r)) {
        const evento = map.get(normalizeCompareText("Evento"));
        if (evento) evento.Total += 1;
        continue;
      }

      if (!isTrasladoTablaRow(r)) continue;

      const socioLabel = resolveSocioFromRow(r) || "Socio directo";
      const socioKey = normalizeCompareText(socioLabel);
      if (!map.has(socioKey)) continue;
      const obj = map.get(socioKey);

      const trasladoKey = resolveTrasladoTipoForTable(r) || "SIN_MEDICO";

      if (obj[trasladoKey] !== undefined) obj[trasladoKey] += 1;
      else obj.SIN_MEDICO += 1;
      obj.Total += 1;
    }

    const totals = list.reduce(
      (acc, r) => {
        acc.UTIM += r.UTIM;
        acc.UTIP += r.UTIP;
        acc.CON_MEDICO += r.CON_MEDICO;
        acc.SIN_MEDICO += r.SIN_MEDICO;
        acc.Total += r.Total;
        return acc;
      },
      { UTIM: 0, UTIP: 0, CON_MEDICO: 0, SIN_MEDICO: 0, Total: 0 },
    );

    const conveniosConMovimientos = list.filter(
      (row) => Number(row.Total || 0) > 0,
    ).length;

    return { list, totals, conveniosConMovimientos };
  }, [analysisRows, trasladosRows]);

  const resumenApi = useMemo(() => {
    const contabilizables = visibleRows.filter((r) => !isBlackRow(r));
    const bases = new Set(contabilizables.map((r) => r.base).filter(Boolean));
    const moviles = new Set(
      contabilizables.map((r) => r.movil).filter(Boolean),
    );

    return {
      total: contabilizables.length,
      bases: bases.size,
      moviles: moviles.size,
      conConvenio: contabilizables.filter((r) => Boolean(r.convenio)).length,
      conTipo: contabilizables.filter((r) => Boolean(r.tipo)).length,
      conPrestador: contabilizables.filter((r) => Boolean(r.prestador)).length,
      conHorarioOperativo: contabilizables.filter((r) =>
        Boolean(r.horarioOperativo),
      ).length,
      tripulaciones: tripulacionesRows.length,
      anulados: anulados.length,
    };
  }, [visibleRows, tripulacionesRows, anulados]);

  const buildJpgExportSheets = () => {
    const fechaActual = formatDateOnlyShort(fechaISO);
    const fechaHasta = formatDateOnlyShort(
      lastRange?.fhasta?.slice?.(0, 10) || fechaISO,
    );
    const fechaTitulo = labelFechaPlanilla(fechaISO);

    const prodHtml = `
      <section class="mito-jpg-page mito-jpg-productividad" data-export-name="01-productividad">
        <table class="mito-jpg-table mito-jpg-prod-top">
          <thead>
            <tr>
              <th colspan="3" class="mito-jpg-black-title">Productividad de ${escapeHtml(operationalRangeLabel)}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="mito-jpg-prod-box">
                <div class="mito-jpg-box-title">Totales por color:</div>
                <div>Rojos: ${escapeHtml(productividadResumen.totColor.ROJO)}</div>
                <div>Amarillos: ${escapeHtml(productividadResumen.totColor.AMARILLO)}</div>
                <div>Verde: ${escapeHtml(productividadResumen.totColor.VERDE)}</div>
                <div>Fucsia: ${escapeHtml(productividadResumen.totColor.FUCSIA)}</div>
                <div>Traslados: ${escapeHtml(productividadResumen.traslados)}</div>
                <div>Eventos: ${escapeHtml(productividadResumen.eventos)}</div>
              </td>
              <td class="mito-jpg-prod-box">
                <div class="mito-jpg-box-title">Derivados a<br/>prestador por edad:</div>
                <div>Adultos: ${escapeHtml(productividadResumen.derPorEdad.Adultos)}</div>
                <div>Pediátricos: ${escapeHtml(productividadResumen.derPorEdad["Pediátricos"])}</div>
              </td>
              <td class="mito-jpg-prod-box">
                <div class="mito-jpg-box-title">Derivados a prestador<br/>por color:</div>
                <div>Rojos: ${escapeHtml(productividadResumen.derPorColor.ROJO)}</div>
                <div>Amarillos: ${escapeHtml(productividadResumen.derPorColor.AMARILLO)}</div>
                <div>Verdes: ${escapeHtml(productividadResumen.derPorColor.VERDE)}</div>
                <div>Fucsia: ${escapeHtml(productividadResumen.derPorColor.FUCSIA || "")}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <table class="mito-jpg-table mito-jpg-info">
          <tbody>
            <tr>
              <td class="mito-jpg-blue-head">Informe de servicios<br/>desde:</td>
              <td>${escapeHtml(fechaActual)}</td>
              <td class="mito-jpg-blue-head">Hasta:</td>
              <td>${escapeHtml(fechaHasta)}</td>
            </tr>
          </tbody>
        </table>

        <table class="mito-jpg-table mito-jpg-totalserv">
          <tbody>
            <tr>
              <td class="mito-jpg-blue-head">Total de Servicios:</td>
              <td class="mito-jpg-softblue mito-jpg-center mito-jpg-bold">${escapeHtml(productividadResumen.totalServicios)}</td>
            </tr>
          </tbody>
        </table>

        <table class="mito-jpg-table mito-jpg-pami">
          <thead>
            <tr>
              <th class="mito-jpg-blue-head">Totales Servicios<br/>PAMI</th>
              <th class="mito-jpg-blue-head">Rojos</th>
              <th class="mito-jpg-blue-head">Amarillos</th>
              <th class="mito-jpg-blue-head">Verdes</th>
              <th class="mito-jpg-blue-head">Celeste</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="mito-jpg-softblue mito-jpg-center mito-jpg-bold">${escapeHtml(productividadResumen.totalPami)}</td>
              <td class="mito-jpg-center">${escapeHtml(productividadResumen.pamiColor.ROJO)}</td>
              <td class="mito-jpg-center">${escapeHtml(productividadResumen.pamiColor.AMARILLO)}</td>
              <td class="mito-jpg-center">${escapeHtml(productividadResumen.pamiColor.VERDE)}</td>
              <td class="mito-jpg-center">${escapeHtml(productividadResumen.pamiColor.CELESTE)}</td>
            </tr>
          </tbody>
        </table>
      </section>
    `;

    const porMovilHead = [
      "BASE",
      "Horario",
      "Moviles",
      "Operativo",
      "Horas Operativas",
      "Realizado de 8 a 18",
      "Realizado 18 a 6",
      "total",
      "Traslados + Derivaciones",
      "Ingresado de 22 a 6",
      "Realizado de 22 a 6",
      "Promedio de atención",
      "Inoperantes",
      "Observaciones de los inoperantes",
    ];
    const porMovilCols = [120, 96, 56, 58, 72, 76, 76, 60, 86, 78, 78, 92, 60, 245];

    const porMovilRowsHtml = porMovilDisplayRows
      .map(
        (r) => `
      <tr>
        ${makeExportCell(r.base, "mito-jpg-softblue mito-jpg-left")}
        ${makeExportCell(
          r.horario || r.horarioDefault || "",
          "mito-jpg-softblue mito-jpg-center",
        )}
        ${makeExportCell(r.movil || "", "mito-jpg-softblue mito-jpg-center")}
        ${makeExportCell(
          r.operativoDisplay,
          "mito-jpg-gray mito-jpg-center mito-jpg-bold mito-jpg-italic",
        )}
        ${makeExportCell(
          r.horasOperativasDisplay,
          "mito-jpg-gray mito-jpg-center mito-jpg-bold",
        )}
        ${makeExportCell(r.realizadosPrimerTramo, "mito-jpg-center")}
        ${makeExportCell(r.realizadosSegundoTramo, "mito-jpg-center")}
        ${makeExportCell(
          r.totalServicios,
          "mito-jpg-yellow mito-jpg-center mito-jpg-bold",
        )}
        ${makeExportCell(
          Number(r.traslados || 0) + Number(r.derivaciones || 0),
          "mito-jpg-center mito-jpg-bold",
        )}
        ${makeExportCell(r.ingresados22a6, "mito-jpg-center")}
        ${makeExportCell(r.realizados22a6, "mito-jpg-center")}
        ${makeExportCell(r.promedioAtencionDisplay, "mito-jpg-center")}
        ${makeExportCell(r.inoperantes, "mito-jpg-center")}
        ${makeExportCell(
          r.observacionesInoperantes,
          "mito-jpg-center mito-jpg-italic mito-jpg-bold",
        )}
      </tr>`,
      )
      .join("");

    const porMovilHtml = `
      <section class="mito-jpg-page mito-jpg-por-movil" data-export-name="02-por-movil">
        <table class="mito-jpg-table mito-jpg-full">
          <colgroup>${porMovilCols.map((width) => `<col style="width:${width}px" />`).join("")}</colgroup>
          <thead>
            <tr>${porMovilHead
              .map((h) => makeExportHeader(h, "mito-jpg-blue-head"))
              .join("")}</tr>
          </thead>
          <tbody>
            ${porMovilRowsHtml}
            <tr class="mito-jpg-total-row">
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              ${makeExportCell(porMovilHorasOperativasTotal, "mito-jpg-center mito-jpg-bold")}
              ${makeExportCell(porMovilTotals.realizadosPrimerTramo, "mito-jpg-center mito-jpg-bold")}
              ${makeExportCell(porMovilTotals.realizadosSegundoTramo, "mito-jpg-center mito-jpg-bold")}
              ${makeExportCell(porMovilTotals.totalServicios, "mito-jpg-center mito-jpg-bold")}
              ${makeExportCell(
                Number(porMovilTotals.traslados || 0) +
                  Number(porMovilTotals.derivaciones || 0),
                "mito-jpg-center mito-jpg-bold",
              )}
              ${makeExportCell(porMovilTotals.ingresados22a6, "mito-jpg-center mito-jpg-bold")}
              ${makeExportCell(porMovilTotals.realizados22a6, "mito-jpg-center mito-jpg-bold")}
              ${makeExportCell(porMovilPromedioAtencionTotal, "mito-jpg-center mito-jpg-bold")}
              ${makeExportCell(porMovilTotals.inoperantes, "mito-jpg-center mito-jpg-bold")}
              <td></td>
            </tr>
          </tbody>
        </table>
      </section>
    `;

    const maxPrestRows = Math.max(
      prestadoresTabla.zona.length,
      prestadoresTabla.fuera.length,
      trasladosTabla.list.length,
    );

    const prestTrasRows = Array.from({ length: maxPrestRows })
      .map((_, i) => {
        const z = prestadoresTabla.zona[i];
        const f = prestadoresTabla.fuera[i];
        const t = trasladosTabla.list[i];
        return `<tr>
          ${makeExportCell(z?.prestador || "", "mito-jpg-softblue mito-jpg-left")}
          ${makeExportCell(z?.total ?? "", "mito-jpg-center")}
          ${makeExportCell(f?.prestador || "", "mito-jpg-softblue mito-jpg-left")}
          ${makeExportCell(f?.total ?? "", "mito-jpg-center")}
          ${makeExportCell(t?.socio || "", "mito-jpg-softblue mito-jpg-left")}
          ${makeExportCell(t?.UTIM ?? "", "mito-jpg-center")}
          ${makeExportCell(t?.UTIP ?? "", "mito-jpg-center")}
          ${makeExportCell(t?.CON_MEDICO ?? "", "mito-jpg-center")}
          ${makeExportCell(t?.SIN_MEDICO ?? "", "mito-jpg-center")}
          ${makeExportCell(t?.Total ?? "", "mito-jpg-softblue mito-jpg-center")}
        </tr>`;
      })
      .join("");

    const prestCols = [120, 50, 120, 50, 165, 52, 52, 72, 72, 60];

    const trasPrestHtml = `
      <section class="mito-jpg-page mito-jpg-tras-prest" data-export-name="03-traslados-prestadores">
        <table class="mito-jpg-table mito-jpg-full">
          <colgroup>${prestCols.map((width) => `<col style="width:${width}px" />`).join("")}</colgroup>
          <thead>
            <tr>
              <th colspan="4" class="mito-jpg-gray-title">REALIZADOS POR PRESTADOR</th>
              <th colspan="6" class="mito-jpg-gray-title">TRASLADOS</th>
            </tr>
            <tr>
              <th colspan="2">EN ZONA MEDIC</th>
              <th colspan="2">FUERA DE ZONA MEDIC</th>
              <th class="mito-jpg-blue-head">SOCIO</th>
              <th class="mito-jpg-blue-head">UTIM</th>
              <th class="mito-jpg-blue-head">UTIP</th>
              <th class="mito-jpg-blue-head">Con medico</th>
              <th class="mito-jpg-blue-head">Sin medico</th>
              <th class="mito-jpg-blue-head">Total</th>
            </tr>
            <tr>
              <th class="mito-jpg-blue-head">PRESTADOR</th>
              <th class="mito-jpg-blue-head">Total</th>
              <th class="mito-jpg-blue-head">PRESTADOR</th>
              <th class="mito-jpg-blue-head">Total</th>
              <th colspan="6" class="mito-jpg-blue-head"></th>
            </tr>
          </thead>
          <tbody>
            ${prestTrasRows}
            <tr class="mito-jpg-total-row">
              <td class="mito-jpg-bold mito-jpg-italic">Sub total:</td>
              <td class="mito-jpg-center">${escapeHtml(prestadoresTabla.subZona)}</td>
              <td class="mito-jpg-bold mito-jpg-italic">Total<br/>&lt;-- --&gt;</td>
              <td class="mito-jpg-center">${escapeHtml(prestadoresTabla.subFuera)}</td>
              <td class="mito-jpg-bold mito-jpg-italic mito-jpg-center">
                Total<br/>${escapeHtml(trasladosTabla.conveniosConMovimientos)} Convenios
              </td>
              <td class="mito-jpg-center">${escapeHtml(trasladosTabla.totals.UTIM)}</td>
              <td class="mito-jpg-center">${escapeHtml(trasladosTabla.totals.UTIP)}</td>
              <td class="mito-jpg-center">${escapeHtml(trasladosTabla.totals.CON_MEDICO)}</td>
              <td class="mito-jpg-center">${escapeHtml(trasladosTabla.totals.SIN_MEDICO)}</td>
              <td class="mito-jpg-center">${escapeHtml(trasladosTabla.totals.Total)}</td>
            </tr>
          </tbody>
        </table>
      </section>
    `;

    const anuladosRowsHtml = anuladosGroupedRows
      .map((group) =>
        group.items
          .map(
            (r, index) => `
      <tr class="${r.isDemora ? "mito-jpg-highlight" : ""}">
        ${
          index === 0
            ? `<td rowspan="${group.items.length}" class="mito-jpg-center">${escapeHtml(group.convenio)}</td><td rowspan="${group.items.length}" class="mito-jpg-center mito-jpg-bold">${escapeHtml(group.total)}</td>`
            : ""
        }
        ${makeExportCell(r.rojo || "", "mito-jpg-center")}
        ${makeExportCell(r.amarillo || "", "mito-jpg-center")}
        ${makeExportCell(r.verde || "", "mito-jpg-center")}
        ${makeExportCell(r.demoraDisplay, "mito-jpg-center")}
        ${makeExportCell(
          formatClockValue(r.horarioAnulacion, hourFormat),
          "mito-jpg-center",
        )}
        ${makeExportCell(r.incidente, "mito-jpg-center")}
        ${makeExportCell(r.observaciones, "mito-jpg-left")}
        ${makeExportCell(r.localidad, "mito-jpg-center")}
      </tr>`,
          )
          .join(""),
      )
      .join("");

    const anuladosCols = [120, 58, 46, 56, 46, 58, 74, 84, 310, 130];

    const anuladosHtml = `
      <section class="mito-jpg-page mito-jpg-anulados" data-export-name="04-anulados">
        <div class="mito-jpg-date-title">${escapeHtml(fechaTitulo)}</div>
        <table class="mito-jpg-table mito-jpg-full">
          <colgroup>${anuladosCols.map((width) => `<col style="width:${width}px" />`).join("")}</colgroup>
          <thead>
            <tr>
              <th rowspan="2">Convenio</th>
              <th rowspan="2">Total</th>
              <th colspan="3">Codificación</th>
              <th rowspan="2">Demora</th>
              <th rowspan="2">Horario de<br/>anulación</th>
              <th rowspan="2">Incidente</th>
              <th rowspan="2">Observaciones</th>
              <th rowspan="2">Localidad</th>
            </tr>
            <tr>
              <th>Rojo</th>
              <th>Amarillo</th>
              <th>Verde</th>
            </tr>
          </thead>
          <tbody>
            ${anuladosRowsHtml}
            <tr class="mito-jpg-total-row">
              <td class="mito-jpg-bold">Total</td>
              <td class="mito-jpg-center mito-jpg-bold">${escapeHtml(anuladosTotals.total)}</td>
              <td class="mito-jpg-center mito-jpg-bold">${escapeHtml(anuladosTotals.rojo)}</td>
              <td class="mito-jpg-center mito-jpg-bold">${escapeHtml(anuladosTotals.amarillo)}</td>
              <td class="mito-jpg-center mito-jpg-bold">${escapeHtml(anuladosTotals.verde)}</td>
              <td colspan="5"></td>
            </tr>
          </tbody>
        </table>
      </section>
    `;

    const anuladosHoraRowsHtml = anuladosPorHora.rows
      .map(
        (r) => `<tr class="${r.label === "Totales:" ? "mito-jpg-total-row" : ""}">
          <td class="mito-jpg-bold mito-jpg-italic">${escapeHtml(r.label)}</td>
          ${anuladosPorHora.buckets
            .map((b) => makeExportCell(r[b] || 0, "mito-jpg-center"))
            .join("")}
          ${makeExportCell(r.Total || 0, "mito-jpg-center mito-jpg-bold")}
        </tr>`,
      )
      .join("");

    const anuladosHoraHtml = `
      <section class="mito-jpg-page mito-jpg-horaria" data-export-name="05-anulados-franja-horaria">
        <table class="mito-jpg-table mito-jpg-full">
          <thead>
            <tr>
              <th>Anulados por<br/>franja Horaria</th>
              ${anuladosPorHora.buckets
                .map((b) =>
                  makeExportHeader(
                    `${formatBucketLabel(b, hourFormat)} Hs`,
                    "mito-jpg-blue-head",
                  ),
                )
                .join("")}
              <th class="mito-jpg-blue-head">Total</th>
            </tr>
          </thead>
          <tbody>${anuladosHoraRowsHtml}</tbody>
        </table>
      </section>
    `;

    return [
      { name: "01-productividad", html: prodHtml },
      { name: "02-por-movil", html: porMovilHtml },
      { name: "03-traslados-prestadores", html: trasPrestHtml },
      { name: "04-anulados", html: anuladosHtml },
      { name: "05-anulados-franja-horaria", html: anuladosHoraHtml },
    ];
  };

  const buildExcelWorkbookXml = () => {
    const xmlEscape = (value) =>
      String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&apos;");

    const makeCell = (cell) => {
      const config =
        cell &&
        typeof cell === "object" &&
        !Array.isArray(cell) &&
        !(cell instanceof Date)
          ? cell
          : { value: cell };
      const value = config.value ?? "";
      const type =
        config.type ||
        (typeof value === "number" && Number.isFinite(value)
          ? "Number"
          : "String");
      const style = config.style ? ` ss:StyleID="${config.style}"` : "";
      const mergeAcross =
        Number.isInteger(config.mergeAcross) && config.mergeAcross > 0
          ? ` ss:MergeAcross="${config.mergeAcross}"`
          : "";
      return `<Cell${style}${mergeAcross}><Data ss:Type="${type}">${xmlEscape(value)}</Data></Cell>`;
    };

    const makeRow = (cells) => `<Row>${(cells || []).map(makeCell).join("")}</Row>`;
    const makeWorksheet = (name, rows) =>
      `<Worksheet ss:Name="${xmlEscape(name)}"><Table>${rows
        .map(makeRow)
        .join("")}</Table></Worksheet>`;

    const excelFechaActual = formatDateOnlyShort(fechaISO);
    const excelFechaHasta = formatDateOnlyShort(
      lastRange?.fhasta?.slice?.(0, 10) || fechaISO,
    );

    const porMovilHeadExcel = [
      "BASE",
      "Horario",
      "Moviles",
      "Operativo",
      "Horas Operativas",
      "Realizado de 8 a 18",
      "Realizado 18 a 6",
      "total",
      "Traslados + Derivaciones",
      "Ingresado de 22 a 6",
      "Realizado de 22 a 6",
      "Promedio de atención",
      "Inoperantes",
      "Observaciones de los inoperantes",
    ];

    const wsProductividad = [
      [
        {
          value: `Productividad de ${operationalRangeLabel}`,
          style: "BlackTitle",
          mergeAcross: 4,
        },
      ],
      [],
      [
        { value: "Totales por color", style: "BlueHeader" },
        { value: "Derivados a prestador por edad", style: "BlueHeader" },
        { value: "Derivados a prestador por color", style: "BlueHeader" },
      ],
      [
        { value: `Rojos: ${productividadResumen.totColor.ROJO}` },
        { value: `Adultos: ${productividadResumen.derPorEdad.Adultos}` },
        { value: `Rojos: ${productividadResumen.derPorColor.ROJO}` },
      ],
      [
        { value: `Amarillos: ${productividadResumen.totColor.AMARILLO}` },
        {
          value: `Pediátricos: ${productividadResumen.derPorEdad["Pediátricos"]}`,
        },
        { value: `Amarillos: ${productividadResumen.derPorColor.AMARILLO}` },
      ],
      [
        { value: `Verdes: ${productividadResumen.totColor.VERDE}` },
        { value: "" },
        { value: `Verdes: ${productividadResumen.derPorColor.VERDE}` },
      ],
      [
        { value: `Fucsia: ${productividadResumen.totColor.FUCSIA}` },
        { value: "" },
        {
          value: `Fucsia: ${productividadResumen.derPorColor.FUCSIA || ""}`,
        },
      ],
      [{ value: `Traslados: ${productividadResumen.traslados}` }, { value: "" }, { value: "" }],
      [{ value: `Eventos: ${productividadResumen.eventos}` }, { value: "" }, { value: "" }],
      [],
      [
        { value: "Desde", style: "BlueHeader" },
        { value: excelFechaActual },
        { value: "Hasta", style: "BlueHeader" },
        { value: excelFechaHasta },
      ],
      [
        { value: "Total de Servicios", style: "BlueHeader" },
        {
          value: productividadResumen.totalServicios,
          style: "Total",
          type: "Number",
        },
      ],
      [],
      [
        { value: "Totales Servicios PAMI", style: "BlueHeader" },
        { value: "Rojos", style: "BlueHeader" },
        { value: "Amarillos", style: "BlueHeader" },
        { value: "Verdes", style: "BlueHeader" },
        { value: "Celeste", style: "BlueHeader" },
      ],
      [
        {
          value: productividadResumen.totalPami,
          style: "SoftBlue",
          type: "Number",
        },
        { value: productividadResumen.pamiColor.ROJO, type: "Number" },
        { value: productividadResumen.pamiColor.AMARILLO, type: "Number" },
        { value: productividadResumen.pamiColor.VERDE, type: "Number" },
        { value: productividadResumen.pamiColor.CELESTE, type: "Number" },
      ],
    ];

    const wsPorMovil = [
      porMovilHeadExcel.map((value) => ({ value, style: "BlueHeader" })),
      ...porMovilDisplayRows.map((r) => [
        { value: r.base, style: "SoftBlue" },
        { value: r.horario || r.horarioDefault || "", style: "SoftBlue" },
        { value: r.movil || "", style: "SoftBlue" },
        { value: r.operativoDisplay, style: "GrayCell" },
        { value: r.horasOperativasDisplay, style: "GrayCell" },
        { value: r.realizadosPrimerTramo, type: "Number" },
        { value: r.realizadosSegundoTramo, type: "Number" },
        { value: r.totalServicios, style: "YellowCell", type: "Number" },
        {
          value: Number(r.traslados || 0) + Number(r.derivaciones || 0),
          type: "Number",
        },
        { value: r.ingresados22a6, type: "Number" },
        { value: r.realizados22a6, type: "Number" },
        { value: r.promedioAtencionDisplay },
        { value: r.inoperantes, type: "Number" },
        { value: r.observacionesInoperantes },
      ]),
      [
        { value: "" },
        { value: "" },
        { value: "" },
        { value: "" },
        { value: porMovilHorasOperativasTotal, style: "Total" },
        {
          value: porMovilTotals.realizadosPrimerTramo,
          style: "Total",
          type: "Number",
        },
        {
          value: porMovilTotals.realizadosSegundoTramo,
          style: "Total",
          type: "Number",
        },
        {
          value: porMovilTotals.totalServicios,
          style: "Total",
          type: "Number",
        },
        {
          value:
            Number(porMovilTotals.traslados || 0) +
            Number(porMovilTotals.derivaciones || 0),
          style: "Total",
          type: "Number",
        },
        {
          value: porMovilTotals.ingresados22a6,
          style: "Total",
          type: "Number",
        },
        {
          value: porMovilTotals.realizados22a6,
          style: "Total",
          type: "Number",
        },
        { value: porMovilPromedioAtencionTotal, style: "Total" },
        {
          value: porMovilTotals.inoperantes,
          style: "Total",
          type: "Number",
        },
        { value: "" },
      ],
    ];

    const maxExcelPrestRows = Math.max(
      prestadoresTabla.zona.length,
      prestadoresTabla.fuera.length,
      trasladosTabla.list.length,
    );

    const wsPrest = [
      [
        {
          value: "Realizados por prestador",
          style: "GrayTitle",
          mergeAcross: 3,
        },
        { value: "Traslados", style: "GrayTitle", mergeAcross: 5 },
      ],
      [
        { value: "Prestador zona", style: "BlueHeader" },
        { value: "Total", style: "BlueHeader" },
        { value: "Prestador fuera", style: "BlueHeader" },
        { value: "Total", style: "BlueHeader" },
        { value: "Socio", style: "BlueHeader" },
        { value: "UTIM", style: "BlueHeader" },
        { value: "UTIP", style: "BlueHeader" },
        { value: "Con medico", style: "BlueHeader" },
        { value: "Sin medico", style: "BlueHeader" },
        { value: "Total", style: "BlueHeader" },
      ],
      ...Array.from({ length: maxExcelPrestRows }).map((_, i) => {
        const z = prestadoresTabla.zona[i];
        const f = prestadoresTabla.fuera[i];
        const t = trasladosTabla.list[i];
        return [
          { value: z?.prestador || "" },
          { value: z?.total ?? "", type: "Number" },
          { value: f?.prestador || "" },
          { value: f?.total ?? "", type: "Number" },
          { value: t?.socio || "" },
          { value: t?.UTIM ?? "", type: "Number" },
          { value: t?.UTIP ?? "", type: "Number" },
          { value: t?.CON_MEDICO ?? "", type: "Number" },
          { value: t?.SIN_MEDICO ?? "", type: "Number" },
          { value: t?.Total ?? "", type: "Number" },
        ];
      }),
      [
        { value: "Sub total", style: "Total" },
        { value: prestadoresTabla.subZona, style: "Total", type: "Number" },
        { value: "Total", style: "Total" },
        { value: prestadoresTabla.subFuera, style: "Total", type: "Number" },
        {
          value: `${trasladosTabla.conveniosConMovimientos} convenios`,
          style: "Total",
        },
        { value: trasladosTabla.totals.UTIM, style: "Total", type: "Number" },
        { value: trasladosTabla.totals.UTIP, style: "Total", type: "Number" },
        {
          value: trasladosTabla.totals.CON_MEDICO,
          style: "Total",
          type: "Number",
        },
        {
          value: trasladosTabla.totals.SIN_MEDICO,
          style: "Total",
          type: "Number",
        },
        { value: trasladosTabla.totals.Total, style: "Total", type: "Number" },
      ],
    ];

    const wsAnulados = [
      [
        {
          value: labelFechaPlanilla(fechaISO),
          style: "SoftBlue",
          mergeAcross: 9,
        },
      ],
      [
        { value: "Convenio", style: "BlueHeader" },
        { value: "Total", style: "BlueHeader" },
        { value: "Rojo", style: "BlueHeader" },
        { value: "Amarillo", style: "BlueHeader" },
        { value: "Verde", style: "BlueHeader" },
        { value: "Demora", style: "BlueHeader" },
        { value: "Horario de anulación", style: "BlueHeader" },
        { value: "Incidente", style: "BlueHeader" },
        { value: "Observaciones", style: "BlueHeader" },
        { value: "Localidad", style: "BlueHeader" },
      ],
      ...anuladosGroupedRows.flatMap((group) =>
        group.items.map((r, index) => [
          { value: index === 0 ? group.convenio : "" },
          { value: index === 0 ? group.total : "", type: "Number" },
          { value: r.rojo || "", type: "Number" },
          { value: r.amarillo || "", type: "Number" },
          { value: r.verde || "", type: "Number" },
          { value: r.demoraDisplay },
          { value: formatClockValue(r.horarioAnulacion, hourFormat) },
          { value: r.incidente },
          { value: r.observaciones },
          { value: r.localidad },
        ]),
      ),
      [
        { value: "Total", style: "Total" },
        { value: anuladosTotals.total, style: "Total", type: "Number" },
        { value: anuladosTotals.rojo, style: "Total", type: "Number" },
        { value: anuladosTotals.amarillo, style: "Total", type: "Number" },
        { value: anuladosTotals.verde, style: "Total", type: "Number" },
        { value: "" },
        { value: "" },
        { value: "" },
        { value: "" },
        { value: "" },
      ],
    ];

    const wsHoraria = [
      [
        { value: "Anulados por franja horaria", style: "BlueHeader" },
        ...anuladosPorHora.buckets.map((b) => ({
          value: `${formatBucketLabel(b, hourFormat)} Hs`,
          style: "BlueHeader",
        })),
        { value: "Total", style: "BlueHeader" },
      ],
      ...anuladosPorHora.rows.map((r) => [
        {
          value: r.label,
          style: r.label === "Totales:" ? "Total" : "DefaultBold",
        },
        ...anuladosPorHora.buckets.map((b) => ({
          value: r[b] || 0,
          style: r.label === "Totales:" ? "Total" : "Center",
          type: "Number",
        })),
        { value: r.Total || 0, style: "Total", type: "Number" },
      ]),
    ];

    return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet" xmlns:html="http://www.w3.org/TR/REC-html40">
  <Styles>
    <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10"/><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/></Style>
    <Style ss:ID="Center"><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10"/></Style>
    <Style ss:ID="DefaultBold"><Alignment ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10" ss:Bold="1"/></Style>
    <Style ss:ID="BlackTitle"><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#000000" ss:Pattern="Solid"/></Style>
    <Style ss:ID="GrayTitle"><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#808080" ss:Pattern="Solid"/></Style>
    <Style ss:ID="BlueHeader"><Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#002060" ss:Pattern="Solid"/></Style>
    <Style ss:ID="SoftBlue"><Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10" ss:Bold="1"/><Interior ss:Color="#BDD7EE" ss:Pattern="Solid"/></Style>
    <Style ss:ID="GrayCell"><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10" ss:Bold="1"/><Interior ss:Color="#D9D9D9" ss:Pattern="Solid"/></Style>
    <Style ss:ID="YellowCell"><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10" ss:Bold="1"/><Interior ss:Color="#FFE699" ss:Pattern="Solid"/></Style>
    <Style ss:ID="Total"><Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders><Font ss:FontName="Arial" ss:Size="10" ss:Bold="1"/><Interior ss:Color="#9DC3E6" ss:Pattern="Solid"/></Style>
  </Styles>
  ${makeWorksheet("01 Productividad", wsProductividad)}
  ${makeWorksheet("02 Por movil", wsPorMovil)}
  ${makeWorksheet("03 Traslados prest", wsPrest)}
  ${makeWorksheet("04 Anulados", wsAnulados)}
  ${makeWorksheet("05 Franja horaria", wsHoraria)}
</Workbook>`;
  };

  const createTemporaryExportRoot = () => {
    const root = document.createElement("div");
    root.className = "mito-jpg-export-root";
    root.innerHTML = buildJpgExportSheets().map((sheet) => sheet.html).join("");
    document.body.appendChild(root);
    return root;
  };

  const exportAllToJPG = async () => {
    if (typeof document === "undefined") return;
    setLoading(true);
    let root = null;
    try {
      const baseName = getExportBaseName(fechaISO);
      let folderHandle = null;

      try {
        folderHandle = await pickExportFolder(baseName);
      } catch (folderError) {
        console.warn(
          "No se pudo seleccionar carpeta, se descargará archivo por archivo.",
          folderError,
        );
      }

      root = createTemporaryExportRoot();
      await new Promise((resolve) => setTimeout(resolve, 80));
      const pages = Array.from(root.querySelectorAll(".mito-jpg-page"));
      for (const page of pages) {
        const name = page.getAttribute("data-export-name") || "planilla";
        const blob = await nodeToJpegBlob(page, 0.98, 2.4);
        const filename = `${baseName}_${name}.jpg`;

        if (folderHandle) {
          await saveBlobToDirectory(folderHandle, filename, blob);
        } else {
          downloadBlob(blob, filename);
          await new Promise((resolve) => setTimeout(resolve, 220));
        }
      }
    } catch (error) {
      console.error("Error exportando JPG:", error);
      setErrorMsg(
        error instanceof Error ? error.message : "No se pudo exportar JPG",
      );
    } finally {
      if (root) root.remove();
      setLoading(false);
    }
  };

  const exportAllToExcel = () => {
    if (typeof document === "undefined") return;
    try {
      const spreadsheetXml = buildExcelWorkbookXml();
      const sheets = parseSpreadsheetXmlToSheets(spreadsheetXml);
      const blob = createXlsxBlobFromSheets(sheets);
      downloadBlob(blob, `${getExportBaseName(fechaISO)}_planillas_excel.xlsx`);
    } catch (error) {
      console.error("Error exportando Excel:", error);
      setErrorMsg(
        error instanceof Error ? error.message : "No se pudo exportar Excel",
      );
    }
  };

  const estadoBody = (row) => {
    const sev =
      row.estado === "FINALIZADO"
        ? "success"
        : row.estado === "EN_CURSO"
          ? "warning"
          : row.estado === "ANULADO"
            ? "danger"
            : "info";

    return (
      <Tag
        value={String(row.estado || "").replaceAll("_", " ") || "—"}
        severity={sev}
      />
    );
  };

  const colorBody = (row) => <ColorBadge color={row.color} />;

  const servicioIdBody = (row) => (
    <span style={{ fontWeight: 600 }}>{row.id || "—"}</span>
  );

  const rangeBody = (row) => (
    <div style={{ display: "grid", gap: 2 }}>
      <span>{formatClockValue(row.hora, hourFormat)}</span>
      <span style={{ color: "#6b7280", fontSize: 12 }}>
        {formatClockValue(row.horaFin, hourFormat)}
      </span>
    </div>
  );

  return (
    <div className="mito-screen">
      <div className="mito-topbar">
        <div className="mito-topbar__left">
          <Button
            type="button"
            label="Volver"
            icon="pi pi-arrow-left"
            className="p-button-sm p-button-text mito-back-btn"
            onClick={() => {
              if (typeof window !== "undefined" && window.history.length > 1) {
                window.history.back();
              }
            }}
          />

          <div className="mito-title">
            <div className="mito-title__h">
              MITO · Servicios finalizados gráficos
            </div>
            <div className="mito-title__sub">
              {labelFecha(fechaISO)} · formato 24 hs · {operationalRangeLabel}
              <span className="mito-dot" />
              <span className="mito-muted">
                Últ. actualización: {formatDateClock(lastUpdate, hourFormat)}
              </span>
            </div>
          </div>
        </div>

        <div className="mito-controls">
          <div className="mito-field">
            <label>Fecha</label>
            <input
              className="mito-date"
              type="date"
              value={fechaISO}
              min={MITO_MIN_DATE}
              onChange={(e) => setFechaISO(clampFechaISO(e.target.value))}
            />
          </div>

          <div className="mito-field">
            <label>Formato</label>
            <input className="mito-select" type="text" value="24 hs" readOnly />
          </div>

          <div className="mito-field">
            <label>Rango operativo</label>
            <input
              className="mito-select"
              type="text"
              value={operationalRangeLabel}
              readOnly
            />
          </div>

          <div className="mito-field">
            <label>Buscar</label>
            <InputText
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="id, móvil, base, localidad..."
              className="mito-search"
            />
          </div>

          <div className="mito-actions">
            <Button
              label={autoRefresh ? "Auto ON" : "Auto OFF"}
              icon={autoRefresh ? "pi pi-refresh" : "pi pi-ban"}
              className={`p-button-sm ${autoRefresh ? "p-button-success" : "p-button-secondary"}`}
              onClick={() => setAutoRefresh((v) => !v)}
            />

            <Button
              label={loading ? "Actualizando..." : "Actualizar"}
              icon="pi pi-sync"
              className="p-button-sm p-button-outlined"
              onClick={fetchData}
              disabled={loading}
            />

            <Button
              label="Descargar JPG"
              icon="pi pi-image"
              className="p-button-sm p-button-success"
              onClick={exportAllToJPG}
              disabled={loading}
            />


            <Button
              label="Productividad"
              icon="pi pi-chart-bar"
              className="p-button-sm"
              onClick={() => setOpenProd(true)}
            />

            <Button
              label={`Anulados (${anulados.length})`}
              icon="pi pi-times-circle"
              className="p-button-sm p-button-danger p-button-outlined"
              onClick={() => setOpenAnu(true)}
            />
          </div>
        </div>
      </div>

      <div className="mito-card" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div
            style={{
              padding: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>Endpoint</div>
            <div style={{ fontWeight: 700 }}>Finalizados gráficos</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {formatDateTimeValue(lastRange.fdesde, hourFormat)} →{" "}
              {formatDateTimeValue(lastRange.fhasta, hourFormat)}
            </div>
          </div>

          <div
            style={{
              padding: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Servicios cargados
            </div>
            <div style={{ fontWeight: 700 }}>{resumenApi.total}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Base para listados automáticos
            </div>
          </div>

          <div
            style={{
              padding: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>Bases únicas</div>
            <div style={{ fontWeight: 700 }}>{resumenApi.bases}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Móviles únicos: {resumenApi.moviles}
            </div>
          </div>

          <div
            style={{
              padding: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Cobertura útil del payload
            </div>
            <div style={{ fontWeight: 700 }}>
              Convenio {resumenApi.conConvenio}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Tipo {resumenApi.conTipo} · Prestador {resumenApi.conPrestador} ·
              Horario {resumenApi.conHorarioOperativo}
              {` · Anulados ${resumenApi.anulados} · Tripulaciones ${resumenApi.tripulaciones}`}
            </div>
          </div>
        </div>

        {errorMsg ? (
          <div
            style={{
              background: "#fff4f4",
              border: "1px solid #fecaca",
              color: "#991b1b",
              padding: 12,
              borderRadius: 12,
              marginBottom: 12,
            }}
          >
            {errorMsg}
          </div>
        ) : null}

        <DataTable
          value={filtered}
          paginator
          rows={12}
          loading={loading}
          stripedRows
          size="small"
          responsiveLayout="scroll"
          emptyMessage="Sin registros para la fecha seleccionada."
        >
          <Column
            header="Servicio"
            body={servicioIdBody}
            style={{ width: 110 }}
          />
          <Column
            header="Tomado / Fin"
            body={rangeBody}
            style={{ width: 130 }}
          />
          <Column header="Color" body={colorBody} style={{ width: 110 }} />
          <Column field="localidad" header="Localidad" style={{ width: 180 }} />
          <Column field="base" header="Base" style={{ width: 160 }} />
          <Column field="movil" header="Móvil" style={{ width: 90 }} />
          <Column header="Estado" body={estadoBody} style={{ width: 130 }} />
          <Column field="tipo" header="Tipo" style={{ width: 120 }} />
          <Column field="convenio" header="Convenio" style={{ width: 160 }} />
          <Column
            field="incidente"
            header="Incidente / detalle"
            style={{ width: 220 }}
          />
        </DataTable>
      </div>

      <Dialog
        header={`Anulados · ${labelFecha(fechaISO)} · ${anulados.length} registros`}
        visible={openAnu}
        style={{ width: "min(1200px, 97vw)" }}
        onHide={() => setOpenAnu(false)}
        draggable={false}
      >
        <TabView>
          <TabPanel header="Anulados">
            <div className="excel-sheet">
              <div className="excel-topline">
                <div className="excel-fecha">
                  {labelFechaPlanilla(fechaISO)}
                </div>
              </div>

              <div className="excel-table-wrap">
                <table className="excel-table excel-anulados">
                  <thead>
                    <tr>
                      <th className="w-conv">Convenio</th>
                      <th className="w-num">Total</th>
                      <th colSpan={3} className="group">
                        Codificación
                      </th>
                      <th className="w-num">Demora</th>
                      <th className="w-hora">Horario de anulación</th>
                      <th className="w-inc">Incidente</th>
                      <th className="w-obs">Observaciones</th>
                      <th className="w-loc">Localidad</th>
                    </tr>
                    <tr>
                      <th />
                      <th />
                      <th className="subhead">Rojo</th>
                      <th className="subhead">Amarillo</th>
                      <th className="subhead">Verde</th>
                      <th />
                      <th />
                      <th />
                      <th />
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {anuladosGroupedRows.map((group) => (
                      <React.Fragment key={group.convenio || "sin-convenio"}>
                        {group.items.map((r, index) => (
                          <tr
                            key={r.key}
                            className={r.isDemora ? "excel-highlight-row" : ""}
                          >
                            {index === 0 ? (
                              <td rowSpan={group.items.length}>
                                {group.convenio}
                              </td>
                            ) : null}
                            {index === 0 ? (
                              <td className="num" rowSpan={group.items.length}>
                                {group.total}
                              </td>
                            ) : null}
                            <td className="num">{r.rojo || ""}</td>
                            <td className="num">{r.amarillo || ""}</td>
                            <td className="num">{r.verde || ""}</td>
                            <td className="num">{r.demoraDisplay}</td>
                            <td className="center">
                              {formatClockValue(r.horarioAnulacion, hourFormat)}
                            </td>
                            <td className="num">{r.incidente}</td>
                            <td>{r.observaciones}</td>
                            <td>{r.localidad}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}

                    <tr className="excel-total-row">
                      <td className="total-label">Total</td>
                      <td className="num">{anuladosTotals.total}</td>
                      <td className="num">{anuladosTotals.rojo}</td>
                      <td className="num">{anuladosTotals.amarillo}</td>
                      <td className="num">{anuladosTotals.verde}</td>
                      <td colSpan={5} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Anulados por hora">
            <div className="excel-sheet">
              <div className="excel-table-wrap">
                <table className="excel-table excel-horaria">
                  <thead>
                    <tr>
                      <th className="w-label">Anulados por franja Horaria</th>
                      {anuladosPorHora.buckets.map((b) => (
                        <th key={b} className="w-slot">
                          {formatBucketLabel(b, hourFormat)} Hs
                        </th>
                      ))}
                      <th className="w-total">Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {anuladosPorHora.rows.map((r) => (
                      <tr
                        key={r.label}
                        className={
                          r.label === "Totales:" ? "excel-total-row" : ""
                        }
                      >
                        <td
                          className={
                            r.label === "Totales:" ? "total-label" : "row-label"
                          }
                        >
                          {r.label}
                        </td>
                        {anuladosPorHora.buckets.map((b) => (
                          <td key={`${r.label}-${b}`} className="num">
                            {r[b] || 0}
                          </td>
                        ))}
                        <td className="num">{r.Total || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Dialog>

      <Dialog
        header={`Productividad · ${labelFecha(fechaISO)} · ${operationalRangeLabel}`}
        visible={openProd}
        className="mito-prod-dialog"
        style={{ width: "min(1740px, 98vw)" }}
        onHide={() => setOpenProd(false)}
        draggable={false}
      >
        <TabView>
          <TabPanel header="Productividad">
            <div className="excel-sheet">
              <div className="excel-titlebar">{`Productividad de ${operationalRangeLabel}`}</div>

              <div className="excel-blocks3">
                <div className="excel-block">
                  <div className="excel-block__head">Totales por color:</div>
                  <div className="excel-kv">
                    <div>Rojos:</div>
                    <div className="num">
                      {productividadResumen.totColor.ROJO}
                    </div>
                    <div>Amarillos:</div>
                    <div className="num">
                      {productividadResumen.totColor.AMARILLO}
                    </div>
                    <div>Verde:</div>
                    <div className="num">
                      {productividadResumen.totColor.VERDE}
                    </div>
                    <div>Fucsia:</div>
                    <div className="num">
                      {productividadResumen.totColor.FUCSIA}
                    </div>
                    <div>Traslados:</div>
                    <div className="num">{productividadResumen.traslados}</div>
                    <div>Eventos:</div>
                    <div className="num">{productividadResumen.eventos}</div>
                    <div className="total-label">Total:</div>
                    <div className="num total-label">
                      {productividadResumen.totalColorResumen}
                    </div>
                  </div>
                </div>

                <div className="excel-block">
                  <div className="excel-block__head">
                    Derivados a prestador por edad:
                  </div>
                  <div className="excel-kv">
                    <div>Adultos:</div>
                    <div className="num">
                      {productividadResumen.derPorEdad.Adultos}
                    </div>
                    <div>Pediátricos:</div>
                    <div className="num">
                      {productividadResumen.derPorEdad["Pediátricos"]}
                    </div>
                    <div className="total-label">Total:</div>
                    <div className="num total-label">
                      {productividadResumen.totalDerPorEdad}
                    </div>
                  </div>
                </div>

                <div className="excel-block">
                  <div className="excel-block__head">
                    Derivados a prestador por color:
                  </div>
                  <div className="excel-kv">
                    <div>Rojos:</div>
                    <div className="num">
                      {productividadResumen.derPorColor.ROJO}
                    </div>
                    <div>Amarillos:</div>
                    <div className="num">
                      {productividadResumen.derPorColor.AMARILLO}
                    </div>
                    <div>Verde:</div>
                    <div className="num">
                      {productividadResumen.derPorColor.VERDE}
                    </div>
                    <div>Fucsia:</div>
                    <div className="num">
                      {productividadResumen.derPorColor.FUCSIA}
                    </div>
                    <div className="total-label">Total:</div>
                    <div className="num total-label">
                      {productividadResumen.totalDerPorColor}
                    </div>
                  </div>
                </div>
              </div>

              <div className="excel-infobar">
                <div className="excel-infobar__label">
                  Informe de servicios desde:
                </div>
                <div className="excel-infobar__value">
                  {formatDateTimeValue(periodDesde, hourFormat)}
                </div>
                <div className="excel-infobar__label">Hasta:</div>
                <div className="excel-infobar__value">
                  {formatDateTimeValue(periodHasta, hourFormat)}
                </div>
              </div>

              <div className="excel-totalserv">
                <div className="excel-totalserv__label">
                  Total de Servicios:
                </div>
                <div className="excel-totalserv__value num">
                  {productividadResumen.totalServicios}
                </div>
              </div>

              <div className="excel-pami">
                <div className="excel-pami__head">
                  Totales Servicios PAMI ({productividadResumen.totalPami})
                </div>
                <div className="excel-pami__cols">
                  <div className="excel-pami__col">Rojos</div>
                  <div className="excel-pami__col">Amarillos</div>
                  <div className="excel-pami__col">Verdes</div>
                  <div className="excel-pami__col">Celeste</div>
                </div>
                <div className="excel-pami__vals">
                  <div className="num">
                    {productividadResumen.pamiColor.ROJO}
                  </div>
                  <div className="num">
                    {productividadResumen.pamiColor.AMARILLO}
                  </div>
                  <div className="num">
                    {productividadResumen.pamiColor.VERDE}
                  </div>
                  <div className="num">
                    {productividadResumen.pamiColor.CELESTE}
                  </div>
                </div>
                <div className="excel-pami__cols">
                  <div className="excel-pami__col">TOTAL</div>
                  <div className="excel-pami__col" />
                  <div className="excel-pami__col" />
                  <div className="excel-pami__col">{productividadResumen.totalPami}</div>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Por movil">
            <div className="excel-sheet">
              <div className="excel-wide-title">
                Productividad según el rango horario de cada movil
              </div>

              <div className="excel-note">
                Los realizados de 6 a 18 y de 18 a 6 se calculan según el
                horario cargado en cada móvil.
              </div>

              <div className="excel-table-wrap">
                <table className="excel-table excel-movil">
                  <thead>
                    <tr>
                      <th className="w-base">BASE</th>
                      <th className="w-hor">Horario</th>
                      <th className="w-movil">Móvil</th>
                      <th className="w-op">Operativo</th>
                      <th className="w-hs">Horas Operativas</th>
                      <th className="w-num">Realizados 6 a 18</th>
                      <th className="w-num">Realizados 18 a 6</th>
                      <th className="w-total">Total</th>
                      <th className="w-deriv">Traslados</th>
                      <th className="w-num">Derivaciones</th>
                      <th className="w-num">Ingresados de 22 a 6</th>
                      <th className="w-num">Realizados de 22 a 6</th>
                      <th className="w-deriv">Promedio de atención</th>
                      <th className="w-num">Inoperantes</th>
                      <th className="w-obs-inop">
                        Observaciones de los inoperantes
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {porMovilDisplayRows.map((r, idx) => (
                      <tr key={`${r.base}-${r.movil || idx}`}>
                        <td className="cell-blue">{r.base}</td>
                        <td className="cell-blue center">
                          <input
                            type="text"
                            value={r.horario}
                            onChange={(e) =>
                              updatePorMovilManual(r, "horario", e.target.value)
                            }
                            className="excel-inline-input"
                            placeholder={r.horarioDefault || ""}
                            autoComplete="off"
                          />
                        </td>
                        <td className="num cell-blue2">{r.movil || ""}</td>
                        <td className="cell-gray center">
                          <input
                            type="text"
                            value={r.operativoDisplay}
                            onChange={(e) =>
                              updatePorMovilManual(
                                r,
                                "operativo",
                                e.target.value,
                              )
                            }
                            className="excel-inline-input excel-inline-input--sm"
                            placeholder=""
                            autoComplete="off"
                          />
                        </td>
                        <td className="cell-gray center">
                          <input
                            type="text"
                            value={r.horasOperativasDisplay}
                            onChange={(e) =>
                              updatePorMovilManual(
                                r,
                                "horasOperativas",
                                e.target.value,
                              )
                            }
                            className="excel-inline-input excel-inline-input--sm"
                            placeholder={formatMinutesAsHourLabel(
                              r.horasOperativasMin,
                            )}
                            autoComplete="off"
                          />
                        </td>
                        <td className="num">{r.realizadosPrimerTramo}</td>
                        <td className="num">{r.realizadosSegundoTramo}</td>
                        <td className="num cell-yellow">{r.totalServicios}</td>
                        <td className="num">{r.traslados}</td>
                        <td className="num">{r.derivaciones}</td>
                        <td className="num">{r.ingresados22a6}</td>
                        <td className="num">{r.realizados22a6}</td>
                        <td className="num">
                          <input
                            type="text"
                            value={r.promedioAtencionDisplay}
                            onChange={(e) =>
                              updatePorMovilManual(
                                r,
                                "promedioAtencion",
                                e.target.value,
                              )
                            }
                            className="excel-inline-input"
                            placeholder={formatAverageAttentionLabel(
                              r.promedioAtencionMin,
                            )}
                            autoComplete="off"
                          />
                        </td>
                        <td className="num">{r.inoperantes}</td>
                        <td className="cell-gray cell-obs-inop">
                          <textarea
                            value={r.observacionesInoperantes}
                            onChange={(e) =>
                              updatePorMovilManual(
                                r,
                                "observacionesInoperantes",
                                e.target.value,
                              )
                            }
                            className="excel-inline-textarea"
                            rows={2}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="excel-total-row">
                      <td className="total-label">Total</td>
                      <td />
                      <td />
                      <td />
                      <td className="num">{porMovilHorasOperativasTotal}</td>
                      <td className="num">
                        {porMovilTotals.realizadosPrimerTramo}
                      </td>
                      <td className="num">
                        {porMovilTotals.realizadosSegundoTramo}
                      </td>
                      <td className="num">{porMovilTotals.totalServicios}</td>
                      <td className="num">{porMovilTotals.traslados}</td>
                      <td className="num">{porMovilTotals.derivaciones}</td>
                      <td className="num">{porMovilTotals.ingresados22a6}</td>
                      <td className="num">{porMovilTotals.realizados22a6}</td>
                      <td className="num">
                        {porMovilPromedioAtencionTotal}
                      </td>
                      <td className="num">{porMovilTotals.inoperantes}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Traslados y prestadores">
            <div className="excel-sheet">
              <div className="excel-table-dual">
                <div className="excel-table-wrap">
                  <table className="excel-table excel-prest">
                    <thead>
                      <tr>
                        <th colSpan={4} className="group gray-head">
                          REALIZADOS POR PRESTADOR
                        </th>
                      </tr>
                      <tr>
                        <th colSpan={2} className="group">
                          EN ZONA MEDIC
                        </th>
                        <th colSpan={2} className="group">
                          FUERA DE ZONA MEDIC
                        </th>
                      </tr>
                      <tr>
                        <th className="subhead">PRESTADOR</th>
                        <th className="subhead w-num">Total</th>
                        <th className="subhead">PRESTADOR</th>
                        <th className="subhead w-num">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {Array.from({
                        length: Math.max(
                          prestadoresTabla.zona.length,
                          prestadoresTabla.fuera.length,
                        ),
                      }).map((_, i) => {
                        const z = prestadoresTabla.zona[i];
                        const f = prestadoresTabla.fuera[i];
                        return (
                          <tr key={i}>
                            <td className="cell-blue">{z?.prestador || ""}</td>
                            <td className="num">{z?.total ?? ""}</td>
                            <td className="cell-blue">{f?.prestador || ""}</td>
                            <td className="num">{f?.total ?? ""}</td>
                          </tr>
                        );
                      })}

                      <tr className="excel-total-row">
                        <td className="total-label">Sub total:</td>
                        <td className="num">{prestadoresTabla.subZona}</td>
                        <td className="total-label">Total</td>
                        <td className="num">{prestadoresTabla.subFuera}</td>
                      </tr>
                      <tr className="excel-total-row">
                        <td className="total-label" colSpan={4}>
                          Total general ← {prestadoresTabla.totalGeneral} →
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="excel-table-wrap">
                  <table className="excel-table excel-trasl">
                    <thead>
                      <tr>
                        <th colSpan={6} className="group gray-head">
                          TRASLADOS
                        </th>
                      </tr>
                      <tr>
                        <th className="subhead w-socio">SOCIO</th>
                        <th className="subhead w-num">UTIM</th>
                        <th className="subhead w-num">UTIP</th>
                        <th className="subhead w-num">Con medico</th>
                        <th className="subhead w-num">Sin medico</th>
                        <th className="subhead w-num">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {trasladosTabla.list.map((r, idx) => (
                        <tr key={`${r.socio}-${idx}`}>
                          <td className="cell-blue">{r.socio}</td>
                          <td className="num cell-gray">{r.UTIM}</td>
                          <td className="num cell-gray">{r.UTIP}</td>
                          <td className="num cell-gray">{r.CON_MEDICO}</td>
                          <td className="num cell-gray">{r.SIN_MEDICO}</td>
                          <td className="num cell-blue2">{r.Total}</td>
                        </tr>
                      ))}

                      <tr className="excel-total-row">
                        <td className="total-label">Total</td>
                        <td className="num">{trasladosTabla.totals.UTIM}</td>
                        <td className="num">{trasladosTabla.totals.UTIP}</td>
                        <td className="num">
                          {trasladosTabla.totals.CON_MEDICO}
                        </td>
                        <td className="num">
                          {trasladosTabla.totals.SIN_MEDICO}
                        </td>
                        <td className="num">{trasladosTabla.totals.Total}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="excel-note">
                    Total {trasladosTabla.conveniosConMovimientos} convenios con
                    movimientos
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Dialog>
    </div>
  );
}
